// Landing styles — TalkPack v3 (per brief April 2026).
// Scope: all selectors prefixed `.lp` so nothing bleeds into the rest of the app.
//
// Build order per §8: this file currently covers steps 1+2 — design tokens,
// nav, trust strip, footer, plus minimal shell. Hero and section styles
// will be added in subsequent iterations.

export const LANDING_STYLES = `
/* ────────── Design tokens (§4.1–§4.4) ────────── */
.lp {
  /* Palette */
  --ink:          #0B0B0F;
  --ink-soft:     #26262C;
  --canvas:       #F7F4EE;
  --paper:        #FFFFFF;
  --accent:       #E8542B;
  --accent-deep:  #B83E1B;
  --signal:       #00A86B;
  --signal-soft:  #E6F6EF;
  --muted:        #6B6B73;
  --muted-soft:   #A7A7AE;
  --line:         #E4DFD5;
  --line-soft:    #EFEBE3;

  /* Module colours */
  --leaf:   #16A34A;   /* Pic-n-Learn */
  --violet: #7C5CFF;   /* Pic-n-Play */
  --blue:   #2563EB;   /* Pic-n-Say */
  --rose:   #E11D48;   /* Pic-n-Win */
  --amber:  #F5A524;   /* Pic-n-Earn */

  /* Type */
  --font-sans: 'Poppins', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  --size-display: 72px;
  --size-h1: 56px;
  --size-h2: 40px;
  --size-h3: 28px;
  --size-h4: 20px;
  --size-body-lg: 18px;
  --size-body: 16px;
  --size-small: 14px;
  --size-micro: 12px;

  /* Spacing (4px base) */
  --space-1: 4px;   --space-2: 8px;   --space-3: 12px;
  --space-4: 16px;  --space-5: 24px;  --space-6: 32px;
  --space-7: 48px;  --space-8: 64px;  --space-9: 96px;
  --space-10: 128px;

  /* Motion */
  --ease-ui:    cubic-bezier(0.22, 1, 0.36, 1);
  --ease-enter: cubic-bezier(0.4, 0, 0.2, 1);
  --dur-micro:  200ms;
  --dur-ui:     400ms;
  --dur-enter:  800ms;

  /* Layout */
  --container: 1280px;
  --gutter: 32px;

  background: var(--canvas);
  color: var(--ink);
  font-family: var(--font-sans);
  font-weight: 400;
  line-height: 1.55;
  font-size: var(--size-body);
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  min-height: 100vh;
}

/* Mobile scale overrides per §4.1 */
@media (max-width: 767px) {
  .lp {
    --size-display: 42px;
    --size-h1: 36px;
    --size-h2: 28px;
    /* Tighten gutter so cards have more room to breathe. */
    --gutter: 16px;
  }
}

/* ────────── Type roles ────────── */
.lp-display {
  font-family: var(--font-sans);
  font-size: var(--size-display);
  font-weight: 700;
  line-height: 1.0;
  letter-spacing: -0.02em;
  color: var(--ink);
  margin: 0;
}
.lp-h1 {
  font-size: var(--size-h1);
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.015em;
  color: var(--ink);
  margin: 0;
}
.lp-h2 {
  font-size: var(--size-h2);
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.015em;
  color: var(--ink);
  margin: 0;
}
.lp-h3 {
  font-size: var(--size-h3);
  font-weight: 600;
  line-height: 1.25;
  letter-spacing: -0.01em;
  color: var(--ink);
  margin: 0;
}
.lp-h4 {
  font-size: var(--size-h4);
  font-weight: 600;
  line-height: 1.35;
  color: var(--ink);
  margin: 0;
}
.lp-body-lg { font-size: var(--size-body-lg); line-height: 1.55; color: var(--ink); }
.lp-body    { font-size: var(--size-body);    line-height: 1.55; color: var(--ink); }
.lp-small   { font-size: var(--size-small);   line-height: 1.5;  color: var(--muted); }
.lp-eyebrow {
  font-size: var(--size-micro);
  font-weight: 600;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--accent);
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.lp-eyebrow::before {
  content: '';
  display: inline-block;
  width: 6px; height: 6px;
  border-radius: 50%;
  background: currentColor;
}

/* ────────── Section scaffold ────────── */
.lp-section {
  padding-block: var(--space-9);
  padding-inline: var(--gutter);
}
.lp-section-inner {
  max-width: var(--container);
  margin-inline: auto;
}
@media (max-width: 1023px) { .lp-section { padding-block: var(--space-8); } }
@media (max-width: 767px)  { .lp-section { padding-block: var(--space-7); } }

/* ────────── Nav bar (§6.1) ────────── */
.lp-nav {
  position: sticky; top: 0; z-index: 50;
  background: var(--canvas);
  border-bottom: 1px solid transparent;
  transition: border-color var(--dur-micro) var(--ease-ui), box-shadow var(--dur-micro) var(--ease-ui);
}
.lp-nav[data-scrolled="true"] {
  border-bottom-color: var(--line);
  box-shadow: 0 1px 0 0 var(--line-soft), 0 8px 24px -16px rgba(11,11,15,0.08);
}
.lp-nav-inner {
  max-width: var(--container);
  margin-inline: auto;
  padding: 16px var(--gutter);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
}
.lp-nav-group-right {
  display: flex; align-items: center; gap: clamp(16px, 2vw, 28px);
}
.lp-logo {
  display: inline-flex; align-items: center; gap: 2px;
  text-decoration: none; color: var(--ink);
  flex-shrink: 0;
}
.lp-logo-shopai {
  /* Asset is white on transparent; invert to black for the cream canvas. */
  filter: invert(1);
  height: 57px;
  width: auto;
  display: block;
  opacity: 0.92;
  /* Trim empty space from both sides of the asset. Negative margins
     pull the cropped image back so the visible logo still starts at
     the container edge and downstream siblings move closer. */
  clip-path: inset(0 52px 0 15px);
  margin-inline-start: -15px;
  margin-inline-end: -52px;
  transform: translateX(-30px);
}
.lp-logo-sep {
  width: 3px;
  height: 52px;
  background: var(--accent);
  display: inline-block;
  position: relative;
  left: -13px;
  margin-top: 4px;
}
.lp-logo-stack {
  display: inline-flex; flex-direction: column; line-height: 1;
  gap: 4px;
  margin-inline-start: 3px;
}
.lp-logo-mark {
  font-family: var(--font-sans);
  font-weight: 800;
  font-size: 38px;
  letter-spacing: -0.025em;
  color: var(--ink);
  line-height: 1;
}
.lp-logo-sub {
  font-size: 13px;
  font-weight: 500;
  color: var(--muted);
  letter-spacing: 0.04em;
  white-space: nowrap;
  line-height: 1;
  text-transform: uppercase;
  position: relative;
  left: 5px;
  top: -5px;
}
/* Nav needs a touch more vertical room once the logo doubles. */
.lp-nav-inner { padding-block: 12px; }
@media (max-width: 1023px) {
  .lp-logo-shopai { height: 44px; }
  .lp-logo-sep { height: 36px; }
  .lp-logo-mark { font-size: 30px; }
  .lp-logo-sub { font-size: 11px; }
}
@media (max-width: 767px) {
  /* Drop the desktop clipping/transform entirely — it cuts the logo off on narrow viewports.
     Scale the whole logo down and let it flow naturally beside the TalkPack wordmark. */
  .lp-logo-shopai {
    height: 28px;
    clip-path: none;
    margin: 0;
    transform: none;
    opacity: 1;
  }
  .lp-logo-sep { height: 22px; left: 0; margin-top: 0; }
  .lp-logo-mark { font-size: 20px; }
  .lp-logo-sub { display: none; }
  .lp-logo-stack { gap: 0; margin-inline-start: 0; }
  .lp-logo { gap: 8px; }
  .lp-nav-inner { padding: 10px 16px; gap: 8px; }
  /* Hide the desktop right-side group; the mobile overlay takes over. */
  .lp-nav-group-right { display: none; }
}

/* Hamburger button — invisible on desktop, 44x44px touch target on mobile. */
.lp-nav-mobile-toggle {
  display: none;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  min-width: 44px;
  border: 1px solid var(--line);
  background: var(--paper);
  color: var(--ink);
  border-radius: 12px;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
}
.lp-nav-mobile-toggle:hover { border-color: var(--ink); color: var(--accent); }
.lp-nav-mobile-toggle:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
@media (max-width: 767px) {
  .lp-nav-mobile-toggle { display: inline-flex; }
}

/* Full-screen mobile overlay menu. */
.lp-mobile-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  max-width: 100vw;
  height: 100vh;
  max-height: 100dvh;
  z-index: 100;
  background: var(--canvas);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overscroll-behavior: contain;
  animation: lp-mobile-overlay-in 200ms var(--ease-enter);
}
@keyframes lp-mobile-overlay-in {
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: none; }
}
.lp-mobile-overlay-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-bottom: 1px solid var(--line);
  background: var(--paper);
  flex-shrink: 0;
}
/* In the overlay head the paper bg matches the toggle bg — give the close button
   more contrast so it doesn't disappear into the header. */
.lp-mobile-overlay-head .lp-nav-mobile-toggle {
  background: var(--canvas);
  border-color: var(--ink-soft);
  color: var(--ink);
}
.lp-mobile-overlay-head .lp-nav-mobile-toggle:hover {
  background: var(--ink);
  color: #fff;
  border-color: var(--ink);
}
.lp-mobile-overlay-body {
  padding: 24px 20px 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
}
.lp-mobile-links {
  display: flex;
  flex-direction: column;
}
.lp-mobile-links a {
  color: var(--ink);
  text-decoration: none;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.01em;
  padding: 14px 4px;
  border-bottom: 1px solid var(--line-soft);
  min-height: 44px;
  display: flex;
  align-items: center;
}
.lp-mobile-links a:last-child { border-bottom: none; }
.lp-mobile-links a:hover,
.lp-mobile-links a:focus-visible { color: var(--accent); }

.lp-mobile-divider {
  height: 1px;
  background: var(--line);
  margin: 8px 0;
}

.lp-mobile-lang { display: flex; flex-direction: column; gap: 8px; }
.lp-mobile-lang-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  width: 100%;
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: 12px;
  color: var(--ink);
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  min-height: 48px;
  text-align: left;
}
.lp-mobile-lang-btn:hover { border-color: var(--ink); }
.lp-mobile-lang-btn .muted { color: var(--muted); font-weight: 400; font-size: 13px; }

.lp-mobile-lang-list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  padding: 6px;
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: 12px;
  max-height: 280px;
  overflow-y: auto;
}
.lp-mobile-lang-list button {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 10px 12px;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: inherit;
  color: var(--ink);
  text-align: left;
  min-height: 48px;
}
.lp-mobile-lang-list button:hover { background: var(--canvas); }
.lp-mobile-lang-list button[data-active="true"] { background: var(--canvas); color: var(--accent); }
.lp-mobile-lang-list button .native { font-weight: 600; font-size: 14px; }
.lp-mobile-lang-list button .label { color: var(--muted); font-size: 11px; }

.lp-mobile-ctas { display: flex; flex-direction: column; gap: 10px; margin-top: auto; padding-top: 8px; }
.lp-mobile-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 52px;
  padding: 14px 20px;
  border-radius: 999px;
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  border: 1px solid transparent;
  letter-spacing: -0.005em;
}
.lp-mobile-cta--primary { background: var(--accent); color: #fff; }
.lp-mobile-cta--primary:hover { background: var(--accent-deep); }
.lp-mobile-cta--secondary { background: var(--paper); color: var(--ink); border-color: var(--line); }
.lp-mobile-cta--secondary:hover { border-color: var(--ink); }
.lp-mobile-cta--ghost { background: transparent; color: var(--ink); border-color: transparent; }
.lp-mobile-cta--ghost:hover { color: var(--accent); }
.lp-mobile-cta:focus-visible { outline: 2px solid var(--accent); outline-offset: 3px; }

.lp-logo--overlay .lp-logo-shopai { height: 28px; clip-path: none; margin: 0; transform: none; opacity: 1; }
.lp-logo--overlay .lp-logo-sep { height: 22px; left: 0; margin-top: 0; }
.lp-logo--overlay .lp-logo-mark { font-size: 20px; }
.lp-logo--overlay .lp-logo-stack { gap: 0; margin-inline-start: 0; }
.lp-logo--overlay { gap: 8px; }
.lp-nav-links {
  display: flex;
  gap: 24px;
  flex-wrap: nowrap;
}
.lp-nav-links .lp-nav-link { white-space: nowrap; }
@media (max-width: 1023px) {
  .lp-nav-links { display: none; }
}
.lp-nav-link {
  font-size: var(--size-small);
  font-weight: 500;
  color: var(--ink);
  text-decoration: none;
  background: transparent;
  border: 0;
  cursor: pointer;
  padding: 8px 2px;
  position: relative;
  transition: color var(--dur-micro) var(--ease-ui);
}
.lp-nav-link:hover { color: var(--accent); }
.lp-nav-link::after {
  content: '';
  position: absolute;
  left: 0; right: 0; bottom: 2px;
  height: 1px;
  background: var(--accent);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform var(--dur-micro) var(--ease-ui);
}
.lp-nav-link:hover::after { transform: scaleX(1); }

/* ────────── Nav dropdowns (Platform / Modules) ──────────
   The nav-link with a dropdown is a <button> styled identically to the
   neighbouring <a> nav-links plus a chevron. The popover is anchored to
   the link via a wrap container with position:relative. */
.lp-nav-link-wrap { position: relative; }
.lp-nav-link--has-dropdown {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  /* The chevron is a tiny visual cue — no rotate-on-open since the menu
     itself appearing is feedback enough and a rotating chevron in the
     condensed nav row attracts more attention than the action deserves. */
}
.lp-nav-link--has-dropdown[aria-expanded="true"] { color: var(--accent); }
.lp-nav-link--has-dropdown[aria-expanded="true"]::after { transform: scaleX(1); }
.lp-nav-link--has-dropdown .chev {
  opacity: 0.6;
  transition: opacity var(--dur-micro) var(--ease-ui);
}
.lp-nav-link--has-dropdown:hover .chev,
.lp-nav-link--has-dropdown[aria-expanded="true"] .chev { opacity: 1; }

.lp-nav-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 220px;
  display: grid;
  gap: 2px;
  padding: 8px;
  background: var(--paper);
  border: 1px solid rgba(11, 11, 15, 0.08);
  border-radius: 12px;
  box-shadow:
    0 16px 40px -12px rgba(11, 11, 15, 0.18),
    0 2px 6px -2px rgba(11, 11, 15, 0.08);
  z-index: 50;
  animation: lp-nav-dropdown-in 180ms var(--ease-enter);
}
.lp-nav-dropdown-item {
  appearance: none;
  background: transparent;
  border: 0;
  padding: 10px 12px;
  text-align: left;
  font: inherit;
  font-size: var(--size-small);
  font-weight: 500;
  color: var(--ink);
  border-radius: 8px;
  cursor: pointer;
  transition:
    background-color var(--dur-micro) var(--ease-ui),
    color var(--dur-micro) var(--ease-ui);
}
.lp-nav-dropdown-item:hover,
.lp-nav-dropdown-item:focus-visible {
  background: rgba(232, 84, 43, 0.08); /* accent at low alpha */
  color: var(--accent);
  outline: none;
}
@keyframes lp-nav-dropdown-in {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ────────── Mobile nav dropdowns (inline expand) ──────────
   On phones the overlay is full-height so a nested popover would feel
   awkward — instead taps on Platform / Modules expand the list inline,
   pushing content below downward. */
.lp-mobile-link {
  appearance: none;
  background: transparent;
  border: 0;
  width: 100%;
  text-align: left;
  font: inherit;
  cursor: pointer;
  /* match the visual weight of the existing <a> rows in lp-mobile-links */
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 0;
  border-bottom: 1px solid rgba(11, 11, 15, 0.08);
  font-size: var(--size-body-lg);
  font-weight: 500;
  color: var(--ink);
  transition: color var(--dur-micro) var(--ease-ui);
}
.lp-mobile-link:hover,
.lp-mobile-link:focus-visible { color: var(--accent); outline: none; }
.lp-mobile-sublist {
  display: grid;
  gap: 2px;
  padding: 4px 0 12px var(--space-3);
  border-bottom: 1px solid rgba(11, 11, 15, 0.08);
}
.lp-mobile-sublist-item {
  appearance: none;
  background: transparent;
  border: 0;
  width: 100%;
  text-align: left;
  font: inherit;
  cursor: pointer;
  padding: 10px 0;
  font-size: var(--size-small);
  color: rgba(11, 11, 15, 0.78);
  transition: color var(--dur-micro) var(--ease-ui);
}
.lp-mobile-sublist-item:hover,
.lp-mobile-sublist-item:focus-visible { color: var(--accent); outline: none; }
[dir="rtl"] .lp-nav-link::after { transform-origin: right; }
.lp-nav-right {
  display: flex; align-items: center; gap: 12px;
}

/* Language button (sits in nav) */
.lp-lang-btn {
  display: inline-flex; align-items: center; gap: 6px;
  background: transparent;
  border: 1px solid var(--line);
  color: var(--ink);
  font-size: var(--size-small);
  font-weight: 500;
  padding: 8px 12px;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background var(--dur-micro) var(--ease-ui), border-color var(--dur-micro) var(--ease-ui);
}
.lp-lang-btn:hover { background: var(--paper); border-color: var(--muted-soft); }
.lp-lang-btn .chev { transition: transform var(--dur-micro) var(--ease-ui); }
.lp-lang-btn[aria-expanded="true"] .chev { transform: rotate(180deg); }

.lp-lang-menu {
  position: absolute;
  top: calc(100% + 8px); inset-inline-end: 0;
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 6px;
  min-width: 220px;
  max-height: 360px;
  overflow-y: auto;
  box-shadow: 0 10px 40px -12px rgba(11,11,15,0.18);
  z-index: 60;
  display: grid;
  gap: 2px;
}
.lp-lang-menu-item {
  display: grid; grid-template-columns: 1fr auto; gap: 12px; align-items: baseline;
  width: 100%;
  background: transparent;
  border: 0;
  padding: 8px 12px;
  border-radius: 8px;
  color: var(--ink);
  font-size: var(--size-small);
  font-weight: 500;
  cursor: pointer;
  text-align: start;
  transition: background var(--dur-micro) var(--ease-ui);
}
.lp-lang-menu-item:hover { background: var(--canvas); }
.lp-lang-menu-item[data-active="true"] { background: var(--signal-soft); color: var(--ink); }
.lp-lang-menu-item .native { font-weight: 600; }
.lp-lang-menu-item .label  { color: var(--muted); font-weight: 400; font-size: 12px; }

/* Register text button — secondary */
.lp-register-link {
  font-size: var(--size-small);
  font-weight: 600;
  color: var(--ink);
  text-decoration: none;
  background: transparent;
  border: 0;
  padding: 8px 10px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: color var(--dur-micro) var(--ease-ui);
}
.lp-register-link:hover { color: var(--accent); }

/* Filled accent CTA (hero + nav replacement usage) */
.lp-cta {
  display: inline-flex; align-items: center; gap: 8px;
  background: var(--accent);
  color: var(--paper);
  font-family: var(--font-sans);
  font-weight: 600;
  font-size: var(--size-body);
  padding: 14px 22px;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background var(--dur-micro) var(--ease-ui), transform var(--dur-micro) var(--ease-ui);
}
.lp-cta:hover { background: var(--accent-deep); }
.lp-cta:active { transform: translateY(1px); }
.lp-cta:focus-visible { outline: 2px solid var(--accent-deep); outline-offset: 3px; }
.lp-cta .arrow { transition: transform var(--dur-micro) var(--ease-ui); }
.lp-cta:hover .arrow { transform: translateX(3px); }
[dir="rtl"] .lp-cta .arrow { transform: scaleX(-1); }
[dir="rtl"] .lp-cta:hover .arrow { transform: scaleX(-1) translateX(3px); }

/* Ghost CTA */
.lp-cta-ghost {
  display: inline-flex; align-items: center; gap: 8px;
  background: transparent;
  color: var(--ink);
  font-family: var(--font-sans);
  font-weight: 600;
  font-size: var(--size-body);
  padding: 14px 18px;
  border: 0;
  cursor: pointer;
  transition: color var(--dur-micro) var(--ease-ui);
  text-decoration: none;
}
.lp-cta-ghost:hover { color: var(--accent); }
.lp-cta-ghost .arrow { transition: transform var(--dur-micro) var(--ease-ui); }
.lp-cta-ghost:hover .arrow { transform: translateX(3px); }

/* ────────── Trust strip (§6.2) ────────── */
.lp-trust {
  background: var(--canvas);
  border-bottom: 1px solid var(--line);
}
.lp-trust-inner {
  max-width: var(--container);
  margin-inline: auto;
  padding: 0 var(--gutter);
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.lp-trust-left, .lp-trust-right {
  font-size: var(--size-small);
  color: var(--muted);
  display: inline-flex; align-items: center; gap: 8px;
  line-height: 1.35;
}
.lp-trust-left .bullet {
  width: 6px; height: 6px; background: var(--signal);
  border-radius: 50%; display: inline-block; flex-shrink: 0;
}

@media (max-width: 767px) {
  .lp-trust-inner {
    flex-direction: column;
    padding-block: 10px;
    align-items: center;
    text-align: center;
  }
}

/* ────────── CTA size variants ────────── */
.lp-cta-lg {
  height: 56px;
  padding: 0 28px;
  font-size: var(--size-body-lg);
}

/* ────────── Hero (§6.3) ────────── */
.lp-hero {
  padding: 30px 0 var(--space-7);
  background: var(--canvas);
}
.lp-hero .lp-eyebrow { font-size: 14.52px; }
.lp-hero .lp-eyebrow::before { background: var(--ink); }
.lp-hero-inner {
  max-width: var(--container);
  margin-inline: auto;
  padding-inline: var(--gutter);
  display: flex;
  align-items: flex-start;
  gap: 20px;
  justify-content: flex-start;
}
.lp-hero-text { flex: 0 1 auto; max-width: 560px; }
.lp-hero-visual-photo { flex: 0 0 auto; }
@media (max-width: 1023px) {
  .lp-hero-inner { flex-direction: column; gap: var(--space-7); align-items: stretch; }
  .lp-hero-text { max-width: none; }
}

.lp-hero-text { display: flex; flex-direction: column; gap: var(--space-5); }
.lp-hero-title {
  font-size: var(--size-display);
  font-weight: 700;
  line-height: 1.0;
  letter-spacing: -0.025em;
  margin: 0;
}
.lp-hero-title .ink { color: var(--ink); }
.lp-hero-title .accent { color: var(--accent); }
.lp-hero-sub {
  font-size: var(--size-body-lg);
  line-height: 1.55;
  color: var(--muted);
  max-width: 560px;
  margin: 0;
}
.lp-hero-ctas {
  display: flex; flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-top: var(--space-3);
}
@media (max-width: 767px) {
  .lp-hero-ctas { width: 100%; }
  .lp-hero-ctas .lp-cta, .lp-hero-ctas .lp-cta-ghost { width: 100%; justify-content: center; }
}

/* Hero visual — shared container */
.lp-hero-visual {
  width: 100%;
  max-width: 560px;
  margin-inline: auto;
  position: relative;
}
.lp-hero-svg { width: 100%; height: auto; display: block; aspect-ratio: 1 / 1; }

/* Hero visual — photo variant (primary) */
.lp-hero-visual-photo {
  margin: 0;
  width: clamp(500px, 48vw, 720px);
  aspect-ratio: 3 / 2;
  border-radius: 20px;
  overflow: hidden;
  box-shadow:
    0 1px 0 0 rgba(11, 11, 15, 0.04),
    0 24px 48px -20px rgba(11, 11, 15, 0.20),
    0 8px 16px -8px rgba(232, 84, 43, 0.18);
  background: var(--line-soft);
  position: relative;
  justify-self: end;
  transform: scale(1.25);
  transform-origin: left top;
}
@media (max-width: 1023px) {
  .lp-hero-visual-photo { width: 100%; max-width: 720px; margin-inline: auto; }
}
.lp-hero-visual-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  display: block;
}
@media (max-width: 1023px) {
  .lp-hero-visual-photo {
    margin-inline: auto;
    aspect-ratio: 16 / 10;
  }
}
@media (max-width: 767px) {
  /* Mobile: kill the desktop transform scale (was pushing the image past the
     viewport edge) and cap the visual height so it doesn't shove body copy
     off-screen. object-fit: cover on the inner <img> centre-crops for us. */
  .lp-hero-visual-photo {
    transform: none;
    width: 100%;
    max-height: 60vh;
    aspect-ratio: 4 / 3;
    border-radius: 16px;
  }
}
.lp-hero-svg .lp-reticle { animation: lp-reticle-pulse 2s ease-in-out infinite; transform-origin: 75px 116px; transform-box: fill-box; }
@keyframes lp-reticle-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.7; transform: scale(1.04); } }

.lp-hero-svg .lp-pill {
  opacity: 0;
  animation: lp-pill-in 6s var(--ease-ui) infinite;
}
.lp-hero-svg .lp-pill-1 { animation-delay: 0.4s; }
.lp-hero-svg .lp-pill-2 { animation-delay: 1.2s; }
.lp-hero-svg .lp-pill-3 { animation-delay: 2.0s; }
@keyframes lp-pill-in {
  0% { opacity: 0; transform: translateY(8px); }
  8%, 70% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-4px); }
}

/* ────────── Stats ticker (§6.4) ────────── */
.lp-ticker {
  background: var(--ink);
  color: var(--paper);
  height: 96px;
  display: flex;
  align-items: center;
  overflow: hidden;
  position: relative;
  border-block: 1px solid var(--line-soft);
}
.lp-ticker-track {
  display: flex;
  gap: 64px;
  white-space: nowrap;
  padding-inline: 32px;
  will-change: transform;
}
.lp-ticker-item {
  display: inline-flex; align-items: center; gap: 12px;
  flex-shrink: 0;
}
.lp-ticker-value {
  font-size: 24px;
  font-weight: 600;
  letter-spacing: -0.025em;
  color: #ffffff;
}
.lp-ticker-label {
  font-size: 13px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.70);
  text-transform: none;
  letter-spacing: 0.01em;
}
.lp-ticker-sep {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent);
  margin-inline-start: 40px;
  flex-shrink: 0;
}
@keyframes lp-ticker-scroll {
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}
/* Respect reduced-motion only on mobile — on desktop keep the scroll so
   marketing surfaces remain animated even in reduced-motion environments
   (preview tooling, many dev laptops). */
@media (prefers-reduced-motion: reduce) and (max-width: 767px) {
  .lp-ticker-track { animation: none; }
  .lp-ticker { overflow-x: auto; }
  .lp-ticker-track > .lp-ticker-item:nth-child(n + 5) { display: none; }
}

/* ────────── Three-step workflow (TL;DR above full journey) ────────── */
.lp-steps {
  position: relative;
  padding: 14px 0 48px;
  background:
    radial-gradient(ellipse at center, rgba(255, 255, 255, 0.04) 0%, rgba(11, 11, 15, 0.04) 75%),
    repeating-linear-gradient(
      0deg,
      rgba(0, 201, 167, 0.025) 0,
      rgba(0, 201, 167, 0.025) 1px,
      transparent 1px,
      transparent 40px
    ),
    var(--canvas);
}
.lp-steps-inner {
  max-width: var(--container);
  margin-inline: auto;
  padding-inline: var(--gutter);
}
.lp-steps-head {
  display: flex; flex-direction: column; gap: 12px;
  max-width: 720px;
  /* 88px clears the 12 o'clock satellite chip (orbits 130 above circle centre
     − chip half-height 32 − circle radius 90 = 72 above the row top) with
     16px breathing room between the sub-paragraph and the top chips. */
  margin: 0 auto 88px;
  text-align: center;
  align-items: center;
}
.lp-steps-title {
  font-size: 44px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: var(--ink);
  margin: 0;
}
.lp-steps-sub {
  font-size: var(--size-body-lg);
  line-height: 1.55;
  color: var(--muted);
  margin: 0;
  max-width: 600px;
}

.lp-steps-row {
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 80px;
  align-items: start;
}
@media (max-width: 1023px) {
  .lp-steps-row { gap: 40px; }
}
@media (max-width: 767px) {
  .lp-steps-row { grid-template-columns: 1fr; gap: 48px; }
  .lp-steps { padding: 48px 0; }
  .lp-steps-title { font-size: 32px; font-weight: 800; }
  .lp-steps-sub { font-size: 16px; line-height: 1.5; }
}

/* Mobile typography scale (global) — applied below 768px per design brief.
   Body minimum stays at 14px for readability. Section heads 32px/w800,
   subs 16px/lh1.5, card titles 18px, card descs 14px/lh1.6, labels 11px. */
@media (max-width: 767px) {
  .lp-hero-sub { font-size: 16px; line-height: 1.55; }
  .lp-journey-title { font-weight: 800; }
  .lp-journey-sub { font-size: 16px; line-height: 1.5; }
  .lp-entry-card-title { font-size: 18px; }
  .lp-entry-card-desc { font-size: 14px; line-height: 1.6; }
  .lp-eyebrow { font-size: 11px; }
}

/* Continuous gradient line + travelling pulse (behind circles) */
.lp-steps-line {
  position: absolute;
  /* Circle centre measured at y=98px inside the row (radius 90 + ~8px step
     offset). Aligns the horizontal data stream across all three circles. */
  top: 98px;
  left: 0; right: 0;
  height: 1px;
  background: linear-gradient(
    90deg,
    rgba(0, 201, 167, 0) 0%,
    rgba(0, 201, 167, 0.3) 15%,
    rgba(0, 201, 167, 0.3) 85%,
    rgba(0, 201, 167, 0) 100%
  );
  z-index: 0;
  pointer-events: none;
}
/* Horizontal binary stream — JS-driven, flows left→right across the three circles.
   Colour shifts from teal (left) through orange (middle) to violet (right). */
.lp-steps-hstream {
  position: absolute;
  top: 50%;
  left: 0; right: 0;
  height: 14px;
  margin-top: -7px;
  pointer-events: none;
  z-index: 0;
  font-family: "Courier New", ui-monospace, monospace;
  font-size: 11px;
  font-weight: 700;
  line-height: 14px;
}
.lp-steps-hstream-char {
  position: absolute;
  top: 0;
  will-change: left, opacity, color;
  text-shadow: 0 0 6px currentColor;
  color: #00D9A3;
  transition: color 200ms linear;
}
@media (max-width: 767px) {
  .lp-steps-line, .lp-steps-pulse { display: none; }
}

/* Step column (now an anchor for clickable navigation) */
.lp-step {
  position: relative;
  z-index: 1;
  display: flex; flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 14px;
  color: inherit;
  text-decoration: none;
  opacity: 0;
  transform: translateY(8px);
  transition: opacity 500ms ease, transform 500ms ease;
}
.lp-steps-inner[data-visible="true"] .lp-step {
  opacity: 1;
  transform: translateY(0);
  transition-delay: calc(var(--i, 0) * 200ms);
}
.lp-step-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent);
}

/* Tactile layered circle — breathes subtly (Layer 1) */
.lp-step-circle {
  --shadow-col: 0, 217, 163;
  width: 180px;
  height: 180px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: #ffffff;
  position: relative;
  z-index: 2;
  background: linear-gradient(135deg, var(--c1) 0%, var(--c2) 100%);
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow:
    inset 0 4px 20px rgba(0, 0, 0, 0.15),
    0 24px 48px rgba(var(--shadow-col), 0.25);
  transition: transform 280ms var(--ease-ui),
              box-shadow 280ms var(--ease-ui);
  animation: lp-step-breathe 4s ease-in-out infinite;
}
.lp-step[data-variant="enter"]  .lp-step-circle { animation-delay: 0s;    }
.lp-step[data-variant="engage"] .lp-step-circle { animation-delay: -1.3s; }
.lp-step[data-variant="learn"]  .lp-step-circle { animation-delay: -2.6s; }
@keyframes lp-step-breathe {
  0%, 100% {
    transform: scale(1);
    box-shadow:
      inset 0 4px 20px rgba(0, 0, 0, 0.15),
      0 24px 48px rgba(var(--shadow-col), 0.25);
  }
  50% {
    transform: scale(1.02);
    box-shadow:
      inset 0 4px 20px rgba(0, 0, 0, 0.15),
      0 28px 60px rgba(var(--shadow-col), 0.38);
  }
}
.lp-step[data-variant="enter"]  { --c1: #00D9A3; --c2: #0E9676; }
.lp-step[data-variant="engage"] { --c1: #E8542B; --c2: #B83E1B; }
.lp-step[data-variant="learn"]  { --c1: #7C5CFF; --c2: #5B3FD9; }
.lp-step[data-variant="enter"]  .lp-step-circle { --shadow-col: 0, 217, 163; }
.lp-step[data-variant="engage"] .lp-step-circle { --shadow-col: 232, 84, 43; }
.lp-step[data-variant="learn"]  .lp-step-circle { --shadow-col: 124, 92, 255; }

/* Specular highlight (top-left glow) */
.lp-step-specular {
  position: absolute;
  top: 8%; left: 8%;
  width: 50%; height: 50%;
  border-radius: 50%;
  background: radial-gradient(
    circle at 30% 30%,
    rgba(255, 255, 255, 0.45) 0%,
    rgba(255, 255, 255, 0) 60%
  );
  pointer-events: none;
  z-index: 2;
}

/* Rotating dashed inner ring (Layer 2) — 25s, alternating direction */
.lp-step-ring {
  position: absolute;
  top: 15%; left: 15%;
  width: 70%; height: 70%;
  border-radius: 50%;
  border: 1px dashed rgba(255, 255, 255, 0.35);
  pointer-events: none;
  animation: lp-step-ring-spin 25s linear infinite;
  z-index: 2;
  transform-origin: center;
}
.lp-step[data-variant="engage"] .lp-step-ring { animation-direction: reverse; }
@keyframes lp-step-ring-spin {
  from { transform: rotate(0deg);   }
  to   { transform: rotate(360deg); }
}

/* Icon SVG composition — pulses when the travelling dot passes behind (Layer 4).
   Pulse cycle matches the travelling-pulse timing (5.5s). Each circle's
   pulse fires at the moment the travelling dot reaches it (roughly
   16.7%, 50%, 83.3% of the journey). */
.lp-step-icon-svg {
  position: relative;
  z-index: 3;
  width: 80%;
  height: 80%;
  color: #ffffff;
  animation: lp-step-icon-pulse 5.5s linear infinite;
}
.lp-step[data-variant="enter"]  .lp-step-icon-svg { animation-delay: 0s;     }
.lp-step[data-variant="engage"] .lp-step-icon-svg { animation-delay: -1.83s; }
.lp-step[data-variant="learn"]  .lp-step-icon-svg { animation-delay: -3.67s; }
@keyframes lp-step-icon-pulse {
  0%, 88%, 100% {
    opacity: 1;
    filter: drop-shadow(0 0 0 rgba(255, 255, 255, 0));
  }
  3% {
    opacity: 1;
    filter: drop-shadow(0 0 6px rgba(255, 255, 255, 0.6));
    transform: scale(1.08);
  }
  8% {
    opacity: 1;
    transform: scale(1);
  }
}
.lp-step-icon-ring {
  transform-box: fill-box;
  transform-origin: center;
  animation: lp-step-ring-pulse 2s ease-out infinite;
  opacity: 0;
}
@keyframes lp-step-ring-pulse {
  0%   { transform: scale(0.5); opacity: 0.9; }
  100% { transform: scale(1.3); opacity: 0;   }
}
.lp-step-sparkle {
  transform-box: fill-box;
  transform-origin: center;
  animation: lp-step-sparkle-pulse 2s ease-in-out infinite;
}
@keyframes lp-step-sparkle-pulse {
  0%, 100% { opacity: 0.8; transform: scale(1);    }
  50%      { opacity: 1;   transform: scale(1.15); }
}
.lp-step-pentagon-dot {
  transform-box: fill-box;
  transform-origin: center;
  animation: lp-step-dot-pulse 2s ease-in-out infinite;
}
@keyframes lp-step-dot-pulse {
  0%, 80%, 100% { opacity: 0.65; transform: scale(1);    }
  30%           { opacity: 1;    transform: scale(1.25); }
}
.lp-step-particle {
  transform-box: fill-box;
  transform-origin: center;
  animation: lp-step-particle-rise 2.5s ease-out infinite;
  opacity: 0;
}
@keyframes lp-step-particle-rise {
  0%   { opacity: 0; transform: translateY(0);     }
  20%  { opacity: 1; }
  100% { opacity: 0; transform: translateY(-20px); }
}

/* Layer 5 — ambient light particles orbiting from the circle centre */
@property --pa {
  syntax: "<angle>";
  inherits: true;
  initial-value: 0deg;
}
.lp-step-particles {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0; height: 0;
  pointer-events: none;
  z-index: 1;
}
.lp-step-particle-orb {
  --pa: 0deg;
  position: absolute;
  top: 0; left: 0;
  width: 2.5px; height: 2.5px;
  border-radius: 50%;
  background: var(--particle-col, rgba(0, 217, 163, 0.4));
  box-shadow: 0 0 4px var(--particle-col, rgba(0, 217, 163, 0.35));
  transform:
    rotate(calc(60deg * var(--i)))
    rotate(var(--pa))
    translateY(calc(-1 * var(--r)));
  animation:
    lp-particle-orbit var(--s, 20s) linear infinite,
    lp-particle-flicker var(--o, 6s) ease-in-out infinite;
  animation-delay: calc(var(--i) * -1.2s), calc(var(--i) * -0.6s);
}
.lp-step[data-variant="enter"]  { --particle-col: rgba(0, 217, 163, 0.4); }
.lp-step[data-variant="engage"] { --particle-col: rgba(232, 110, 70, 0.4); }
.lp-step[data-variant="learn"]  { --particle-col: rgba(140, 110, 255, 0.4); }
@keyframes lp-particle-orbit {
  from { --pa: 0deg;   }
  to   { --pa: 360deg; }
}
@keyframes lp-particle-flicker {
  0%, 100% { opacity: 0.1; }
  50%      { opacity: 0.4; }
}

/* Satellite chips — statically positioned around each circle at clock angles.
   Thin binary streams flow from each chip inward to the parent circle. */
.lp-step-sats {
  position: absolute;
  /* Circle is now the first child of .lp-step (label was removed earlier),
     so its vertical centre = radius = 90px. This makes the chip halo
     geometrically centred on the circle. */
  top: 90px;
  left: 50%;
  width: 0; height: 0;
  z-index: 3;
}
.lp-step-sat-slot {
  --a: 0deg;
  --r: 130px;
  position: absolute;
  top: 0; left: 0;
  transform: rotate(var(--a)) translateY(calc(-1 * var(--r)));
  width: 0; height: 0;
}
/* The binary stream — pointing from chip-centre toward circle (i.e. "down"
   in the rotated local frame). Chars flow along the length. */
.lp-step-stream {
  position: absolute;
  top: 34px;  /* chip half-height + small gap */
  left: 50%;
  width: 12px;
  height: 38px;
  transform: translateX(-50%);
  pointer-events: none;
  font-family: "Courier New", ui-monospace, monospace;
  font-size: 9px;
  font-weight: 700;
  color: var(--stream-col, rgba(0, 217, 163, 0.7));
  text-align: center;
  line-height: 1;
  overflow: hidden;
}
.lp-step[data-variant="enter"]  { --stream-col: rgba(0, 217, 163, 0.7);  }
.lp-step[data-variant="engage"] { --stream-col: rgba(232, 84, 43, 0.7);  }
.lp-step[data-variant="learn"]  { --stream-col: rgba(124, 92, 255, 0.75); }

.lp-step-stream-char {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  text-shadow: 0 0 4px currentColor;
  animation: lp-stream-flow 4s linear infinite;
  animation-delay: calc(var(--i, 0) * -1.33s);
}
@keyframes lp-stream-flow {
  0%   { top: -10px;  opacity: 0; }
  10%  { opacity: 0.8; }
  85%  { opacity: 0.6; }
  100% { top: 38px;   opacity: 0; }
}

/* Chip body — counter-rotated so it reads upright at any angle */
.lp-step-sat-chip {
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%) rotate(calc(-1 * var(--a)));
  width: 64px;
  height: 64px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 4px;
  transition: transform 200ms var(--ease-ui), box-shadow 220ms ease;
  cursor: default;
}
.lp-step-sat-icon {
  color: var(--sat-accent, #00C9A7);
  display: inline-flex;
}
.lp-step[data-variant="enter"]  .lp-step-sat-chip { --sat-accent: #00C9A7; }
.lp-step[data-variant="engage"] .lp-step-sat-chip { --sat-accent: #E8542B; }
.lp-step[data-variant="learn"]  .lp-step-sat-chip { --sat-accent: #7C5CFF; }
.lp-step-sat-label {
  font-size: 10px;
  font-weight: 600;
  color: #1a1a1a;
  letter-spacing: 0.01em;
  line-height: 1.15;
  text-align: center;
  /* Allow labels to wrap to 2 lines inside the chip when the word is long.
     overflow-wrap: anywhere lets compound words like "Engagement" or
     "Conversation" break mid-word as a last resort. */
  white-space: normal;
  overflow-wrap: anywhere;
  max-width: 100%;
}

/* The LEARN variant has longer labels (Shoppers, Conversation, Location,
   Engagement, Compliance). Widen those chips so the label has room to
   breathe without wrapping awkwardly. ENTER/ENGAGE keep the compact size. */
.lp-step[data-variant="learn"] .lp-step-sat-chip {
  width: 92px;
  padding: 0 8px;
}

/* Hover — lift chip, accelerate its stream */
.lp-step-sat-slot:hover .lp-step-sat-chip {
  transform: translate(-50%, -50%) rotate(calc(-1 * var(--a))) translateY(-2px);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12), 0 0 18px var(--stream-col);
}
.lp-step-sat-slot:hover .lp-step-stream-char { animation-duration: 1.3s; opacity: 1; }

/* Step-level hover accelerates ALL streams + horizontal stream */
.lp-step:hover .lp-step-stream-char { animation-duration: 2s; }

@media (max-width: 1023px) {
  .lp-step-sats { top: 70px; }
  .lp-step-sat-slot { --r: 120px; }
  .lp-step-sat-chip { width: 56px; height: 56px; }
  .lp-step-stream { top: 30px; height: 26px; }
}

.lp-step-title {
  font-size: 24px;
  font-weight: 800;
  color: var(--ink);
  /* Unified 112px across all three variants so Enter, Engage, and Learn
     titles (plus their descriptions) align on the same baseline. ENTER's
     6 o'clock chip extends ~72px below the circle — 112px clears it plus
     gives the other two variants matching breathing room. */
  margin: 112px 0 0;
  letter-spacing: -0.015em;
}
.lp-step-desc {
  font-size: 14px;
  font-weight: 400;
  color: var(--muted);
  line-height: 1.6;
  margin: 0;
  max-width: 240px;
  text-align: center;
}
.lp-step-more {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: var(--accent);
  opacity: 0;
  transform: translateY(4px);
  transition: opacity 200ms var(--ease-ui), transform 200ms var(--ease-ui);
}

/* Hover state (Layer 6) — override breathing, brighten, accelerate rings */
.lp-step:hover .lp-step-circle {
  animation: none;
  transform: scale(1.05);
  box-shadow:
    inset 0 4px 20px rgba(0, 0, 0, 0.15),
    0 32px 64px rgba(var(--shadow-col), 0.4);
}
.lp-step:hover .lp-step-ring { animation-duration: 12s; }
.lp-step:hover .lp-step-icon-svg { filter: brightness(1.2); }
.lp-step:hover .lp-step-labels { color: var(--labels-col); opacity: 1; filter: brightness(1.1); }
.lp-step:hover ~ * .lp-steps-pulse,
.lp-steps-row:has(.lp-step:hover) .lp-steps-pulse { animation-duration: 3s; }
.lp-step:hover .lp-step-particle-orb { animation-duration: 8s, var(--o); opacity: 0.75 !important; }
.lp-step:hover .lp-step-more {
  opacity: 1;
  transform: translateY(0);
}

@media (max-width: 1023px) {
  .lp-step-circle { width: 140px; height: 140px; }
  .lp-step-title { font-size: 20px; }
  /* Tablet circle is 140px → centre = 70px. */
  .lp-steps-line { top: 70px; }
}
/* Hide mobile-only scaffolding on desktop (default; mobile media query below re-enables). */
.lp-step-pills { display: none; }
.lp-step-mobile-chevron { display: none; }

@media (max-width: 767px) {
  .lp-step-circle { width: 120px; height: 120px; }
  .lp-steps-line, .lp-steps-pulse, .lp-steps-hstream { display: none; }
  .lp-step-labels { font-size: 11px; }

  /* Drop the satellite chip orbit on mobile — it doesn't fit and becomes noisy.
     The label-pill row below the title conveys the same information compactly. */
  .lp-step-sats { display: none; }

  /* Stack circles vertically, centre-aligned, with breathing room. */
  .lp-steps-row { gap: 28px; }
  .lp-step { gap: 10px; padding: 0 4px; }
  .lp-step-title { font-size: 24px; font-weight: 700; margin-top: 4px; }
  .lp-step-desc { font-size: 14px; line-height: 1.6; max-width: 320px; margin: 0 auto; }

  /* Mobile-only label pill row — middot-separated, tinted per variant at 0.75 opacity. */
  .lp-step-pills {
    display: block;
    font-size: 11px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-weight: 600;
    line-height: 1.6;
    max-width: 300px;
    margin: 0 auto;
    word-spacing: 2px;
  }
  .lp-step[data-variant="enter"]  .lp-step-pills { color: rgba(0, 217, 163, 0.85); }
  .lp-step[data-variant="engage"] .lp-step-pills { color: rgba(232, 84, 43, 0.85); }
  .lp-step[data-variant="learn"]  .lp-step-pills { color: rgba(124, 92, 255, 0.85); }

  /* Dashed vertical connector + downward chevron between stacked circles. */
  .lp-step-mobile-chevron {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    margin-top: 16px;
    position: relative;
    color: rgba(0, 217, 163, 0.7);
  }
  .lp-step-mobile-chevron::before {
    content: "";
    position: absolute;
    top: -18px;
    left: 50%;
    transform: translateX(-50%);
    width: 1px;
    height: 18px;
    background-image: linear-gradient(to bottom, rgba(0, 217, 163, 0.6) 50%, transparent 50%);
    background-size: 1px 6px;
    background-repeat: repeat-y;
  }
}
/* Reduced-motion: the workflow's animations are all subtle (breathing,
   slow orbits, travelling pulse), so we only disable the sharp per-icon
   flashes. Everything else keeps its "live system" feel. */
@media (prefers-reduced-motion: reduce) {
  .lp-step-icon-ring,
  .lp-step-sparkle,
  .lp-step-pentagon-dot,
  .lp-step-particle,
  .lp-step-icon-svg { animation: none !important; }
  .lp-step { opacity: 1 !important; }
}

/* ────────── Integrations / Built-on strip ────────── */
.lp-integrations {
  background: var(--canvas);
  padding: 56px 0;
  border-top: 1px solid var(--line-soft);
}
.lp-integrations-inner {
  max-width: var(--container);
  margin-inline: auto;
  padding-inline: var(--gutter);
}
.lp-integrations-head {
  display: flex; flex-direction: column; gap: 10px;
  max-width: 720px;
  margin: 0 auto 40px;
  text-align: center;
  align-items: center;
}
.lp-integrations-title {
  font-size: 36px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: var(--ink);
  margin: 0;
}
.lp-integrations-sub {
  font-size: var(--size-body-lg);
  line-height: 1.55;
  color: var(--muted);
  margin: 0;
  max-width: 640px;
}
.lp-integrations-grid {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 14px;
}
@media (max-width: 1279px) {
  .lp-integrations-grid { grid-template-columns: repeat(4, 1fr); }
}
@media (max-width: 1023px) {
  .lp-integrations-grid { grid-template-columns: repeat(3, 1fr); }
}
@media (max-width: 767px) {
  .lp-integrations-grid { grid-template-columns: repeat(2, 1fr); }
  .lp-integrations { padding: 48px 0; }
  .lp-integrations-title { font-size: 28px; }
}
.lp-integration-tile {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 12px;
  height: 160px;
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 16px;
  opacity: 0;
  transform: translateY(6px);
  transition: opacity 400ms ease, transform 400ms ease, box-shadow 200ms ease;
}
.lp-integrations-inner[data-visible="true"] .lp-integration-tile {
  opacity: 1;
  transform: translateY(0);
  transition-delay: calc(var(--i, 0) * 100ms);
}
.lp-integration-tile:hover {
  box-shadow: 0 6px 18px -10px rgba(11, 11, 15, 0.15);
}
.lp-integration-logo {
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: rgba(11, 11, 15, 0.75);
  line-height: 1;
  white-space: nowrap;
  max-height: 40px;
  display: inline-flex; align-items: center;
}
.lp-integration-tile[data-variant="shopai"]    .lp-integration-logo { font-family: var(--font-sans); }
.lp-integration-tile[data-variant="aws"]       .lp-integration-logo {
  font-family: "Segoe UI", system-ui, sans-serif;
  font-weight: 900;
  font-size: 24px;
  letter-spacing: -0.04em;
}
.lp-integration-tile[data-variant="aws"]       .lp-integration-logo::after {
  content: "";
  display: inline-block;
  width: 18px; height: 6px;
  margin-left: 6px;
  background: rgba(11, 11, 15, 0.75);
  border-radius: 3px;
  position: relative;
  transform: rotate(-10deg);
}
.lp-integration-tile[data-variant="anthropic"] .lp-integration-logo {
  font-family: serif;
  font-weight: 600;
  letter-spacing: 0.02em;
  font-size: 20px;
}
.lp-integration-tile[data-variant="openai"]    .lp-integration-logo {
  font-family: var(--font-sans);
  font-weight: 600;
  letter-spacing: -0.005em;
  font-size: 22px;
}
.lp-integration-tile[data-variant="openai"]    .lp-integration-logo::before {
  content: "";
  display: inline-block;
  width: 18px; height: 18px;
  margin-right: 6px;
  border-radius: 50%;
  border: 2.5px solid rgba(11, 11, 15, 0.75);
  vertical-align: -3px;
}
.lp-integration-tile[data-variant="google"]    .lp-integration-logo {
  font-family: "Product Sans", var(--font-sans);
  font-weight: 500;
  font-size: 24px;
  letter-spacing: -0.01em;
}
.lp-integration-tile[data-variant="openfoodfacts"] .lp-integration-logo {
  font-size: 15px;
  font-weight: 700;
  text-align: center;
  white-space: normal;
  line-height: 1.1;
  max-width: 130px;
}
.lp-integration-tile[data-variant="stripe"]    .lp-integration-logo {
  font-family: var(--font-sans);
  font-weight: 700;
  font-size: 26px;
  letter-spacing: -0.025em;
  color: #635BFF;
}
.lp-integration-caption {
  font-size: 11px;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  text-align: center;
}
.lp-integrations-footnote {
  margin: 40px auto 0;
  text-align: center;
  font-size: 13px;
  color: var(--muted);
  line-height: 1.55;
  white-space: nowrap;
}
@media (max-width: 1023px) {
  .lp-integrations-footnote {
    white-space: normal;
    max-width: 720px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .lp-integration-tile { opacity: 1 !important; transform: none !important; transition: none; }
}

/* ────────── Full Journey infographic (§6.5) ────────── */
.lp-journey {
  background: var(--ink);
  color: var(--paper);
  padding: 24px 0 48px;
}
.lp-journey-inner {
  max-width: 1440px;
  margin-inline: auto;
  padding-inline: var(--gutter);
}
.lp-journey-head {
  display: flex; flex-direction: column; gap: 8px;
  margin-bottom: 24px;
  max-width: 720px;
}
.lp-journey-head .lp-eyebrow { color: var(--accent); }
.lp-journey-head .lp-eyebrow::before { background: var(--accent); }
.lp-journey-title {
  font-size: 44px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: #ffffff;
  margin: 0;
}
.lp-journey-sub {
  font-size: var(--size-body-lg);
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.72);
  margin: 0;
}

/* Shell — unified container wrapping the three columns + flow strip */
.lp-journey-shell {
  position: relative;
  background:
    radial-gradient(
      ellipse at center,
      rgba(0, 201, 167, 0.03) 0%,
      rgba(0, 201, 167, 0) 70%
    );
  border: 1px solid rgba(0, 201, 167, 0.15);
  border-radius: 24px;
  padding: 32px 40px;
}

/* Chevron arrows live as grid items between columns. */
.lp-journey-chevron {
  align-self: start;
  justify-self: center;
  margin-top: 14px; /* vertical centre of 48px numbered circle */
  display: inline-flex;
}

.lp-journey-grid {
  position: relative;
  display: grid;
  grid-template-columns: 22fr auto 38fr auto 40fr;
  gap: 24px;
  align-items: stretch;
}
@media (max-width: 1279px) {
  .lp-journey-grid { grid-template-columns: 1fr 1fr; }
  .lp-journey-col-dash { grid-column: 1 / -1; }
  .lp-journey-chevron { display: none; }
}
@media (max-width: 767px) {
  .lp-journey { padding: 32px 0; }
  .lp-journey-shell {
    padding: 24px 16px;
    /* Removed the explicit 2px vertical teal line on mobile — it was
       bleeding through content at x=39-41px. The stacked 1-col layout
       already conveys vertical flow via scroll order + chevrons. */
    background: radial-gradient(
      ellipse at center,
      rgba(0, 201, 167, 0.03) 0%,
      rgba(0, 201, 167, 0) 70%
    );
  }
  .lp-journey-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 28px;
  }
  .lp-journey-col { min-width: 0; max-width: 100%; }
  .lp-journey-col-dash { grid-column: auto; }
  /* Show chevrons on mobile too, but rotated 90° to point down. */
  .lp-journey-chevron {
    display: flex;
    grid-column: 1;
    justify-self: start;
    margin-left: 16px;
    margin-top: 0;
    transform: rotate(90deg);
  }
  .lp-journey-col-head {
    position: relative;
    z-index: 1;
  }
  .lp-journey-col-num {
    background: var(--ink);
    width: 40px;
    height: 40px;
    font-size: 16px;
  }
  .lp-journey-col-title { font-size: 22px; }
  .lp-journey-title { font-size: 32px; font-weight: 800; }
  .lp-entry-card-title { font-size: 18px; }
  .lp-entry-card-desc { font-size: 14px; line-height: 1.6; }
  .lp-exp-phone { width: 80px; height: 160px; border-radius: 22px; }
  .lp-exp-phone-speaker { width: 28px; height: 4px; }
  .lp-exp-phone-screen { width: 70px; height: 136px; border-radius: 16px; }
  .lp-exp-triple { grid-template-columns: 1fr; }
  .lp-journey-flow { display: none; }
  .lp-dash-tabs {
    overflow-x: auto;
    scroll-snap-type: x proximity;
    flex-wrap: nowrap;
    -webkit-overflow-scrolling: touch;
  }
  .lp-dash-tab { scroll-snap-align: start; flex: 0 0 auto; min-width: 96px; }
  .lp-exp-phone-view, .lp-pv-ring, .lp-pv-wheel { animation-duration: 4s; }
}
@media (max-width: 479px) {
  .lp-dash-tile { flex: 1 1 100%; min-width: 0; }
}
.lp-journey-col {
  position: relative;
  z-index: 1;
  display: flex; flex-direction: column;
  gap: 16px;
}

/* Numbered column header — circle marker + title */
.lp-journey-col-head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 0 0 4px;
}
.lp-journey-col-num {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid #00C9A7;
  background: transparent;
  color: #00C9A7;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.02em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 18px rgba(0, 201, 167, 0.22);
}
.lp-journey-col-title {
  font-size: 22px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  letter-spacing: -0.015em;
  line-height: 1.2;
}

/* Bottom flow strip — travelling dot reinforces the 1→2→3 sequence */
.lp-journey-flow {
  margin-top: 40px;
  display: flex; flex-direction: column; gap: 10px;
}
.lp-journey-flow-line {
  position: relative;
  height: 1px;
  width: 100%;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
}
.lp-journey-flow-dot {
  position: absolute;
  top: 50%;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #00D9A3;
  box-shadow:
    0 0 12px rgba(0, 217, 163, 0.8),
    0 0 24px rgba(0, 217, 163, 0.35);
  transform: translate(-50%, -50%);
  transition: left 200ms linear;
  pointer-events: none;
}
.lp-journey-flow-dot::after {
  content: "";
  position: absolute;
  right: 100%;
  top: 50%;
  width: 20px;
  height: 2px;
  margin-top: -1px;
  background: linear-gradient(
    90deg,
    rgba(0, 217, 163, 0) 0%,
    rgba(0, 217, 163, 0.5) 100%
  );
  border-radius: 2px;
  filter: blur(0.5px);
  opacity: 0.7;
  pointer-events: none;
}

.lp-journey-flow-labels > span[data-active="true"] {
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 0 8px rgba(0, 217, 163, 0.4);
  transform: scale(1.02);
  transform-origin: left center;
}
.lp-journey-flow-labels > span { transition: color 300ms ease, text-shadow 300ms ease, transform 300ms ease; }

/* Numbered column markers brighten in sync with flow stop */
.lp-journey-col-num {
  transition: color 300ms ease, border-color 300ms ease, box-shadow 300ms ease, background 300ms ease;
}
.lp-journey-col-num[data-active="true"] {
  color: #00F5B3;
  border-color: #00F5B3;
  background: rgba(0, 217, 163, 0.12);
  box-shadow: 0 0 22px rgba(0, 217, 163, 0.55);
  animation: lp-journey-num-pulse 800ms ease-out;
}
@keyframes lp-journey-num-pulse {
  0%   { transform: scale(1);    }
  40%  { transform: scale(1.06); }
  100% { transform: scale(1);    }
}

/* Reduced-motion: tone down the pulse scale on the marker but keep the
   dot and label-highlight so the 1→2→3 story still reads. */
@media (prefers-reduced-motion: reduce) {
  .lp-journey-col-num[data-active="true"] { animation: none; }
  .lp-journey-flow-labels > span { transition: color 500ms ease; }
}
.lp-journey-flow-labels {
  display: grid;
  grid-template-columns: 22fr auto 38fr auto 40fr;
  gap: 24px;
}
.lp-journey-flow-labels > span {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.4);
}
.lp-journey-flow-labels > span[aria-hidden] { width: 12px; }
@media (max-width: 1279px) {
  .lp-journey-flow-labels { grid-template-columns: 1fr 1fr 1fr; }
  .lp-journey-flow-labels > span[aria-hidden] { display: none; }
}
@media (max-width: 767px) {
  .lp-journey-flow-labels { grid-template-columns: 1fr; gap: 4px; }
}

/* Placeholder cards — used in later-stage columns during scaffolding */
.lp-journey-placeholder {
  min-height: 200px;
  border-radius: 16px;
  border: 1px dashed rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.02);
  display: flex; align-items: center; justify-content: center;
  color: rgba(255, 255, 255, 0.35);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.lp-journey-placeholder.is-tall { min-height: 640px; }

/* Entry card — Column 1: horizontal icon+title, description below */
.lp-entry-card {
  position: relative;
  padding: 20px 22px;
  border-radius: 16px;
  background: linear-gradient(180deg, #141418 0%, #0F0F13 100%);
  border: 1px solid rgba(0, 201, 167, 0.35);
  display: flex; flex-direction: column; gap: 12px;
  overflow: hidden;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 0 0 1px rgba(0, 201, 167, 0.15),
    0 8px 32px rgba(0, 0, 0, 0.4),
    0 0 48px rgba(0, 201, 167, 0.08);
}
.lp-entry-card::before {
  content: "";
  position: absolute;
  inset: 0 0 auto 0;
  height: 6px;
  background: var(--accent-bar, #00C9A7);
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}
.lp-entry-card-head {
  display: flex; align-items: center; gap: 16px;
  margin-top: 6px;
}
.lp-entry-card-icon {
  color: #ffffff;
  display: inline-flex; align-items: center;
  flex-shrink: 0;
}
.lp-entry-card-title {
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  letter-spacing: -0.01em;
  line-height: 1.2;
}
.lp-entry-card-desc {
  font-size: 13px;
  font-weight: 400;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
}
@media (max-width: 767px) {
  .lp-entry-card-desc { font-size: 14px; line-height: 1.6; }
  .lp-entry-card-title { font-size: 18px; }
}
.lp-entry-card-pills {
  display: flex; flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}
.lp-entry-card-pill {
  font-size: 11px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  padding: 4px 10px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 999px;
  line-height: 1;
  letter-spacing: 0.01em;
  white-space: nowrap;
}

/* Experience top — two sub-columns: left (header + Verify) / right (phone) */
.lp-exp-top {
  display: flex;
  flex-direction: row;
  gap: 24px;
  align-items: flex-start;
}
.lp-exp-top-left {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.lp-exp-top-right {
  width: 110px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
}
.lp-exp-verify { padding-left: 0; }

/* Experience column — proper device mockup with scan viewfinder */
.lp-exp-phone {
  align-self: center;
  width: 100px;
  height: 200px;
  border-radius: 28px;
  background: #0A0A0D;
  border: 2px solid rgba(0, 201, 167, 0.6);
  box-shadow:
    0 0 24px rgba(0, 201, 167, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);
  display: flex; flex-direction: column; align-items: center;
  padding-top: 14px;
  position: relative;
}
.lp-exp-phone-speaker {
  width: 34px; height: 5px;
  background: #1a1a20;
  border-radius: 999px;
  margin-bottom: 5px;
}
.lp-exp-phone-screen {
  position: relative;
  width: 88px;
  height: 172px;
  border-radius: 20px;
  background: radial-gradient(circle at 50% 50%, rgba(0, 201, 167, 0.08), transparent 60%), #0A0A0D;
  overflow: hidden;
  margin-top: -2px;
}
.lp-exp-phone-svg {
  position: absolute;
  inset: 0;
  width: 100%; height: 100%;
}
.lp-exp-phone-scanline {
  position: absolute;
  left: 18px;
  right: 18px;
  top: 50px;
  height: 2px;
  background: linear-gradient(90deg, transparent 0%, #00D9A3 50%, transparent 100%);
  box-shadow: 0 0 8px rgba(0, 217, 163, 0.8);
  border-radius: 2px;
  animation: lp-exp-scan 2s ease-in-out infinite;
  will-change: transform;
}
@keyframes lp-exp-scan {
  0%, 100% { transform: translateY(0); opacity: 0.9; }
  50%      { transform: translateY(108px); opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .lp-exp-phone-scanline { animation: none; }
}

/* Phone micro-view — fades in on tab change; each view has its own animation */
.lp-exp-phone-view {
  position: absolute;
  inset: 0;
  animation: lp-pv-fade 300ms ease-out;
}
@keyframes lp-pv-fade {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* Shared screen scaffolding */
.lp-pv-screen {
  position: absolute;
  inset: 0;
  padding: 6px 7px;
  display: flex;
  flex-direction: column;
  color: #ffffff;
  font-family: var(--font-sans);
  overflow: hidden;
}
.lp-pv-brand {
  font-size: 7px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #ffffff;
  text-align: center;
  padding: 2px 0 4px;
  text-transform: uppercase;
}
.lp-pv-brand--row { display: flex; align-items: center; justify-content: center; gap: 4px; }
.lp-pv-ai-dot {
  width: 5px; height: 5px; border-radius: 50%;
  background: #00D9A3;
  box-shadow: 0 0 4px rgba(0, 217, 163, 0.8);
}
.lp-pv-cta-btn {
  align-self: stretch;
  margin: 4px 4px 2px;
  height: 15px;
  padding: 0 6px;
  border-radius: 7px;
  background: linear-gradient(180deg, #00D9A3, #00A86B);
  border: 0;
  color: #0A0A0D;
  font-size: 7px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  box-shadow: 0 0 8px rgba(0, 217, 163, 0.5);
  cursor: default;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 15px;
}
.lp-pv-cta-btn--gold {
  background: linear-gradient(180deg, #FFB84D, #C2851A);
  box-shadow: 0 0 8px rgba(255, 184, 77, 0.5);
}

/* View 1 — Prize claim form */
.lp-pv-prize { gap: 3px; }
.lp-pv-prize-head {
  display: flex; align-items: center; justify-content: center; gap: 3px;
  margin-top: 2px;
}
.lp-pv-trophy { display: inline-flex; width: 10px; height: 10px; }
.lp-pv-trophy svg { width: 100%; height: 100%; }
.lp-pv-prize-title {
  font-size: 8px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.01em;
}
.lp-pv-prize-sub {
  font-size: 6px;
  color: rgba(255, 255, 255, 0.65);
  text-align: center;
  line-height: 1.3;
}
.lp-pv-prize-form { display: flex; flex-direction: column; gap: 3px; margin-top: 3px; }
.lp-pv-prize-field {
  display: flex; align-items: center; gap: 4px;
  padding: 3px 5px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  opacity: 0;
  animation: lp-pv-field-in 3s ease-in-out infinite;
}
@keyframes lp-pv-field-in {
  0%, 15% { opacity: 0; transform: translateX(-3px); }
  25%, 85% { opacity: 1; transform: translateX(0); }
  100% { opacity: 0; }
}
.lp-pv-prize-label {
  font-size: 4px;
  color: rgba(255,255,255,0.5);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: block;
  width: 28px;
  flex-shrink: 0;
}
.lp-pv-prize-value {
  font-size: 5.5px;
  color: #ffffff;
  flex: 1;
  font-weight: 500;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  display: inline-flex; align-items: center;
}
.lp-pv-prize-cursor {
  display: inline-block;
  width: 0.8px; height: 6px;
  background: #00D9A3;
  margin-left: 1px;
  animation: lp-pv-cursor 0.8s ease-in-out infinite;
}
@keyframes lp-pv-cursor {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
}
.lp-pv-prize-tick {
  width: 8px; height: 8px;
  background: #00D9A3;
  border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.lp-pv-prize-tick svg { width: 6px; height: 6px; }

.lp-pv-footnote {
  font-size: 4.5px;
  color: rgba(255, 255, 255, 0.45);
  text-align: center;
  letter-spacing: 0.03em;
  margin-top: 2px;
}

/* View 2 — AI Ambassador chat */
.lp-pv-chat { padding: 5px 5px; gap: 3px; }
.lp-pv-chat-header {
  display: flex; align-items: center; gap: 4px;
  padding: 2px 2px 4px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.lp-pv-ai-mark { width: 10px; height: 10px; display: inline-flex; flex-shrink: 0; }
.lp-pv-ai-mark svg { width: 100%; height: 100%; }
.lp-pv-chat-head-text { display: flex; flex-direction: column; line-height: 1.15; }
.lp-pv-chat-title { font-size: 6.5px; font-weight: 700; color: #ffffff; letter-spacing: 0.01em; }
.lp-pv-chat-sub { font-size: 5px; color: rgba(255, 255, 255, 0.55); }
.lp-pv-chat-thread { flex: 1; display: flex; flex-direction: column; gap: 2px; justify-content: flex-end; padding: 0 1px; }
.lp-pv-chat-bubble {
  font-size: 5.2px;
  padding: 2.5px 5px;
  border-radius: 7px;
  max-width: 82%;
  line-height: 1.25;
  opacity: 0;
  animation: lp-pv-chat-in 5s ease-in-out infinite;
}
.lp-pv-chat-bubble--user {
  align-self: flex-end;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.88);
  border-bottom-right-radius: 2px;
}
.lp-pv-chat-bubble--ai {
  align-self: flex-start;
  background: #00D9A3;
  color: #0A0A0D;
  font-weight: 600;
  border-bottom-left-radius: 2px;
  box-shadow: 0 0 6px rgba(0, 217, 163, 0.4);
}
@keyframes lp-pv-chat-in {
  0%, 6% { opacity: 0; transform: translateY(3px); }
  12%, 85% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-2px); }
}
.lp-pv-chat-input {
  display: flex; align-items: center; gap: 4px;
  padding: 3px 5px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 7px;
  border: 1px solid rgba(255, 255, 255, 0.06);
}
.lp-pv-chat-placeholder { flex: 1; font-size: 5px; color: rgba(255, 255, 255, 0.4); }
.lp-pv-chat-send {
  width: 10px; height: 10px;
  background: #00D9A3;
  border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.lp-pv-chat-send svg { width: 6px; height: 6px; }

/* View 3 — Store locator map */
.lp-pv-map { gap: 2px; padding: 4px 4px; }
.lp-pv-map-title {
  font-size: 6.5px;
  font-weight: 700;
  color: #ffffff;
  text-align: center;
  padding: 2px 0 3px;
}
.lp-pv-map-canvas {
  flex: 1;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.02);
  overflow: hidden;
  position: relative;
  min-height: 0;
}
.lp-pv-map-svg { width: 100%; height: 100%; display: block; }
.lp-pv-map-ring {
  transform-box: fill-box;
  transform-origin: center;
  animation: lp-pv-map-ring 2s ease-out infinite;
  opacity: 0;
}
.lp-pv-map-alert {
  animation: lp-pv-map-alert 4s ease-in-out infinite;
  transform-box: fill-box;
  transform-origin: center;
}
@keyframes lp-pv-map-ring {
  0%   { transform: scale(0.5); opacity: 0.9; }
  100% { transform: scale(3);   opacity: 0;   }
}
@keyframes lp-pv-map-alert {
  0%, 80%, 100% { opacity: 1; transform: scale(1); }
  85%           { opacity: 0.5; transform: scale(1.25); }
  90%           { opacity: 1; transform: scale(1); }
}
.lp-pv-map-card {
  margin-top: 3px;
  padding: 3px 4px;
  background: rgba(90, 169, 255, 0.08);
  border: 1px solid rgba(90, 169, 255, 0.3);
  border-radius: 5px;
  display: flex; flex-direction: column; gap: 2px;
}
.lp-pv-map-card-row { display: flex; align-items: center; gap: 4px; }
.lp-pv-map-dot { width: 4px; height: 4px; border-radius: 50%; background: #5AA9FF; box-shadow: 0 0 4px #5AA9FF; flex-shrink: 0; }
.lp-pv-map-label { flex: 1; color: #fff; font-weight: 600; font-size: 5.5px; }
.lp-pv-cta-btn--blue {
  background: linear-gradient(180deg, #5AA9FF, #2563EB);
  box-shadow: 0 0 8px rgba(90, 169, 255, 0.5);
  height: 12px;
  font-size: 5.5px;
  margin: 0;
}

/* View 4 — Spin wheel game */
.lp-pv-wheel-view { gap: 3px; padding: 4px 5px; }
.lp-pv-wheel-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 1px 2px 3px;
}
.lp-pv-wheel-title { font-size: 6px; font-weight: 700; color: #fff; letter-spacing: 0.02em; }
.lp-pv-credits { font-size: 5.5px; font-weight: 700; color: #FFB84D; letter-spacing: 0.04em; text-shadow: 0 0 4px rgba(255, 184, 77, 0.5); }
.lp-pv-wheel-wrap {
  flex: 1;
  position: relative;
  display: flex; align-items: center; justify-content: center;
  overflow: hidden;
}
.lp-pv-wheel-stage {
  width: 70px;
  height: 76px;
  margin: 0 auto;
  position: relative;
}
.lp-pv-wheel-svg { width: 100%; height: 100%; display: block; }
.lp-pv-wheel-disc {
  transform-box: fill-box;
  transform-origin: center;
  animation: lp-pv-wheel-spin 8s cubic-bezier(0.22, 1, 0.36, 1) infinite;
}
@keyframes lp-pv-wheel-spin {
  0%     { transform: rotate(0deg); }
  45%    { transform: rotate(-20deg); }
  60%    { transform: rotate(180deg); }
  75%    { transform: rotate(900deg); }
  100%   { transform: rotate(900deg); }
}
/* Floating coins */
.lp-pv-coins {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.lp-pv-coin {
  position: absolute;
  bottom: 0;
  width: 5px; height: 5px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #FFD97A, #FFB84D);
  box-shadow: 0 0 4px rgba(255, 184, 77, 0.7);
  animation: lp-pv-coin-rise 2.5s ease-out infinite;
  opacity: 0;
}
@keyframes lp-pv-coin-rise {
  0%   { opacity: 0; transform: translateY(0) scale(0.8); }
  20%  { opacity: 1; }
  100% { opacity: 0; transform: translateY(-60px) scale(0.5); }
}
.lp-pv-cta-btn--gold {
  background: linear-gradient(180deg, #FFB84D, #C2851A);
  box-shadow: 0 0 8px rgba(255, 184, 77, 0.5);
  animation: lp-pv-gold-pulse 2.5s ease-in-out infinite;
}
@keyframes lp-pv-gold-pulse {
  0%, 100% { box-shadow: 0 0 8px rgba(255, 184, 77, 0.5); }
  50%      { box-shadow: 0 0 14px rgba(255, 184, 77, 0.9); }
}

/* View 5 — HFSS nutrition */
.lp-pv-nut { gap: 2px; padding: 4px 5px; }
.lp-pv-nut-title {
  font-size: 6.5px;
  font-weight: 700;
  color: #fff;
  text-align: center;
  padding: 1px 0 2px;
  letter-spacing: 0.02em;
}
.lp-pv-nut-toggles {
  display: flex; gap: 3px; justify-content: center;
  margin-bottom: 2px;
}
.lp-pv-nut-toggle {
  font-size: 5px;
  padding: 1.5px 6px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.55);
  letter-spacing: 0.02em;
}
.lp-pv-nut-toggle[data-active="true"] {
  background: rgba(0, 217, 163, 0.18);
  border-color: rgba(0, 217, 163, 0.5);
  color: #00F5B3;
  font-weight: 700;
}
.lp-pv-nut-wrap { flex: 1; display: flex; align-items: center; justify-content: center; min-height: 0; }
.lp-pv-nut-svg { width: 100%; height: 100%; max-height: 72px; display: block; }
.lp-pv-nut-slice {
  opacity: 0;
  transform-origin: 44px 38px;
  transform-box: fill-box;
  animation: lp-pv-nut-cycle 4s ease-in-out infinite;
}
@keyframes lp-pv-nut-cycle {
  0%, 10%  { opacity: 0; transform: scale(0.6); }
  18%, 80% { opacity: 1; transform: scale(1);   }
  100%     { opacity: 0; transform: scale(1);   }
}
.lp-pv-nut-legend {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2px;
  margin: 2px 0;
}
.lp-pv-nut-legend span {
  display: flex; flex-direction: column; align-items: center; gap: 1px;
  font-size: 4.5px;
  color: rgba(255, 255, 255, 0.75);
  letter-spacing: 0.02em;
}
.lp-pv-nut-legend i { width: 5px; height: 5px; border-radius: 50%; display: block; }
.lp-pv-nut-legend strong { font-weight: 500; font-size: 4px; text-transform: uppercase; color: rgba(255,255,255,0.6); }
.lp-pv-nut-legend b { font-weight: 700; color: #ffffff; font-size: 5.5px; }

@media (prefers-reduced-motion: reduce) {
  .lp-pv-prize-field,
  .lp-pv-prize-cursor,
  .lp-pv-chat-bubble,
  .lp-pv-map-ring,
  .lp-pv-map-alert,
  .lp-pv-wheel-disc,
  .lp-pv-coin,
  .lp-pv-cta-btn--gold,
  .lp-pv-nut-slice {
    animation: none;
    opacity: 1;
    transform: none;
  }
}

/* Shared: form rows for Shopper view */
.lp-pv-row {
  opacity: 0;
  animation: lp-pv-row-in 0.5s ease-out forwards;
}
@keyframes lp-pv-row-in {
  from { opacity: 0; transform: translateX(-6px); }
  to   { opacity: 1; transform: translateX(0); }
}
.lp-pv-cta {
  opacity: 0;
  animation: lp-pv-cta-in 0.4s ease-out 1.8s forwards;
}
@keyframes lp-pv-cta-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}

/* Chat bubbles for Conversation view */
.lp-pv-bubble {
  opacity: 0;
  animation: lp-pv-bubble-in 0.35s ease-out forwards;
}
@keyframes lp-pv-bubble-in {
  from { opacity: 0; transform: translateY(6px) scale(0.95); transform-origin: bottom left; }
  to   { opacity: 1; transform: translateY(0)    scale(1);    transform-origin: bottom left; }
}

/* Expanding radar rings for Distribution view */
.lp-pv-ring {
  transform-origin: 52.5px 105px;
  animation: lp-pv-ring-pulse 3s ease-out infinite;
  opacity: 0;
}
@keyframes lp-pv-ring-pulse {
  0%   { transform: scale(0.3); opacity: 0.9; }
  80%  { transform: scale(4);   opacity: 0; }
  100% { transform: scale(4);   opacity: 0; }
}
.lp-pv-pin {
  animation: lp-pv-pin-pulse 3s ease-in-out infinite;
  transform-box: fill-box;
  transform-origin: 50% 100%;
}
@keyframes lp-pv-pin-pulse {
  0%, 100% { transform: translateY(0) scale(1); }
  50%      { transform: translateY(-2px) scale(1.05); }
}

/* Spin wheel for Campaign view */
.lp-pv-wheel {
  transform-origin: 52.5px 95px;
  animation: lp-pv-wheel-spin 3s cubic-bezier(0.22, 1, 0.36, 1) infinite;
}
@keyframes lp-pv-wheel-spin {
  0%   { transform: rotate(0deg); }
  70%  { transform: rotate(1080deg); }
  100% { transform: rotate(1080deg); }
}
.lp-pv-win {
  opacity: 0;
  transform-box: fill-box;
  transform-origin: 50% 50%;
  animation: lp-pv-win-pop 3s ease-out infinite;
}
@keyframes lp-pv-win-pop {
  0%, 70% { opacity: 0; transform: scale(0.6); }
  80%     { opacity: 1; transform: scale(1.1); }
  95%     { opacity: 1; transform: scale(1);   }
  100%    { opacity: 0; transform: scale(1);   }
}

/* Nutrition slices + tick for Compliance view */
.lp-pv-slice {
  opacity: 0;
  transform-origin: 52.5px 95px;
  animation: lp-pv-slice-in 0.4s ease-out forwards;
}
@keyframes lp-pv-slice-in {
  from { opacity: 0; transform: scale(0.85); }
  to   { opacity: 1; transform: scale(1);    }
}
.lp-pv-tick {
  opacity: 0;
  animation: lp-pv-tick-in 0.4s ease-out 1.4s forwards;
}
@keyframes lp-pv-tick-in {
  from { opacity: 0; transform: scale(0.6); transform-origin: center; }
  to   { opacity: 1; transform: scale(1);   }
}

@media (prefers-reduced-motion: reduce) {
  .lp-exp-phone-view,
  .lp-pv-row,
  .lp-pv-cta,
  .lp-pv-bubble,
  .lp-pv-ring,
  .lp-pv-pin,
  .lp-pv-wheel,
  .lp-pv-win,
  .lp-pv-slice,
  .lp-pv-tick { animation: none; opacity: 1; }
}

.lp-exp-steps {
  position: relative;
  display: flex; flex-direction: column;
  gap: 12px;
  padding-left: 24px;
  margin-top: 4px;
}
.lp-exp-steps::before {
  content: "";
  position: absolute;
  left: 10px; top: 8px; bottom: 8px;
  width: 2px;
  background: linear-gradient(
    to bottom,
    rgba(0, 201, 167, 0.0) 0%,
    rgba(0, 201, 167, 0.6) 12%,
    rgba(0, 201, 167, 0.6) 88%,
    rgba(0, 201, 167, 0.0) 100%
  );
  box-shadow: 0 0 12px rgba(0, 201, 167, 0.45);
  border-radius: 2px;
}
.lp-exp-step { display: flex; flex-direction: column; gap: 6px; }
.lp-exp-step-label {
  font-size: 16px;
  font-weight: 500;
  color: #ffffff;
  letter-spacing: -0.005em;
}
.lp-exp-step-text {
  font-size: 13px;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
}

/* Experience cards share one treatment; variant data-attr swaps colours. */
.lp-exp-card {
  --exp-color: #16A34A;
  background: linear-gradient(180deg, #141418 0%, #0F0F13 100%);
  border: 1px solid color-mix(in srgb, var(--exp-color) 45%, transparent);
  border-radius: 14px;
  padding: 14px 18px;
  display: flex; flex-direction: column; gap: 8px;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 0 0 1px color-mix(in srgb, var(--exp-color) 20%, transparent),
    0 8px 32px rgba(0, 0, 0, 0.4),
    0 0 48px color-mix(in srgb, var(--exp-color) 14%, transparent);
}
.lp-exp-card[data-variant="learn"] { --exp-color: #16A34A; }
.lp-exp-card[data-variant="play"]  { --exp-color: #7C5CFF; }
.lp-exp-card[data-variant="earn"]  { --exp-color: #2563EB; }
.lp-exp-card[data-variant="say"]   { --exp-color: #F43F5E; }
.lp-exp-card[data-variant="win"]   { --exp-color: #F5A524; }

.lp-exp-pill {
  align-self: center;
  padding: 4px 12px;
  border-radius: 999px;
  background: linear-gradient(
    180deg,
    var(--exp-color) 0%,
    color-mix(in srgb, var(--exp-color) 70%, #000) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1.2;
  white-space: nowrap;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.25),
    0 0 20px color-mix(in srgb, var(--exp-color) 40%, transparent);
}
.lp-exp-card-body {
  font-size: 13px;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
}

.lp-exp-triple {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.lp-exp-triple .lp-exp-card { padding: 12px 10px; gap: 6px; }
.lp-exp-triple .lp-exp-card-body { font-size: 12px; line-height: 1.45; }
.lp-exp-triple .lp-exp-pill { padding: 3px 10px; font-size: 11px; }

.lp-exp-capture {
  font-size: 13px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.85);
  text-align: center;
  margin: 0;
  padding: 8px 0;
}

/* Capture step — data-point chip grid */
.lp-capture-sub {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
  margin: 0 0 12px;
  letter-spacing: 0.01em;
}
.lp-capture-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  width: 100%;
}
@media (max-width: 767px) {
  .lp-capture-grid { grid-template-columns: repeat(2, 1fr); }
}
.lp-capture-chip {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 8px;
  background: rgba(0, 201, 167, 0.08);
  border: 1px solid rgba(0, 201, 167, 0.25);
  border-radius: 8px;
  min-width: 0;
  opacity: 0;
  transform: translateY(6px);
  transition: opacity 400ms ease, transform 400ms ease,
              box-shadow 180ms ease, background 180ms ease;
}
.lp-capture-grid[data-visible="true"] .lp-capture-chip {
  opacity: 1;
  transform: translateY(0);
  transition-delay: calc(var(--i, 0) * 150ms);
}
.lp-capture-chip:hover {
  background: rgba(0, 201, 167, 0.14);
  box-shadow: 0 0 12px rgba(0, 201, 167, 0.2);
}
.lp-capture-icon {
  color: #00D9A3;
  display: inline-flex;
  flex-shrink: 0;
}
.lp-capture-label {
  font-size: 12px;
  font-weight: 500;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.005em;
}

/* Subtle 8-second teal ripple through visible chips */
.lp-capture-grid[data-visible="true"] .lp-capture-chip {
  animation: lp-capture-ripple 8s linear infinite;
  animation-delay: calc(var(--i, 0) * 1s);
}
@keyframes lp-capture-ripple {
  0%, 90%, 100% { box-shadow: none; background: rgba(0, 201, 167, 0.08); }
  95%           { box-shadow: 0 0 14px rgba(0, 217, 163, 0.4);
                  background: rgba(0, 201, 167, 0.18); }
}
@media (prefers-reduced-motion: reduce) {
  .lp-capture-chip { opacity: 1; transform: none; transition: none; animation: none !important; }
}

@media (max-width: 767px) {
  .lp-exp-triple { grid-template-columns: 1fr; }
}

/* Dashboard column — cycling tabs (5 sub-sections, 22 tiles total) */
.lp-dash {
  border: 2px solid rgba(0, 201, 167, 0.55);
  border-radius: 16px;
  padding: 24px 28px;
  background: #0E0E13;
  box-shadow: 0 0 32px rgba(0, 201, 167, 0.2);
  display: flex; flex-direction: column;
  gap: 16px;
  flex: 1;
  min-height: 0;
}
.lp-dash-cycle { min-height: 420px; }

/* Tab strip — five equal tabs, pill background on active, progress bar at bottom */
.lp-dash-tabs {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}
.lp-dash-tab {
  position: relative;
  flex: 1;
  text-align: center;
  font-size: 13px;
  padding: 10px 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  background: transparent;
  border: 0;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
  letter-spacing: 0;
  border-radius: 6px;
  transition: background 300ms ease, color 300ms ease, box-shadow 300ms ease;
}
.lp-dash-tab:hover { color: rgba(255, 255, 255, 0.75); }
.lp-dash-tab[data-active="true"] {
  color: #ffffff;
  font-weight: 600;
  background: rgba(0, 201, 167, 0.12);
  box-shadow:
    inset 0 0 0 1px rgba(0, 201, 167, 0.3),
    0 0 12px rgba(0, 201, 167, 0.2);
}
.lp-dash-tab:focus-visible {
  outline: 2px solid rgba(0, 217, 163, 0.6);
  outline-offset: 2px;
}
.lp-dash-tab-label {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* Progress bar: 2px gradient line at the bottom of the active pill,
   fills left-to-right over the 4s cycle. */
.lp-dash-tab-progress {
  position: absolute;
  left: 6px;
  right: auto;
  bottom: 0;
  height: 2px;
  width: calc(var(--fill, 0%) * (100% - 12px) / 100);
  max-width: calc(100% - 12px);
  background: linear-gradient(90deg, #00D9A3, #00F5B3);
  box-shadow: 0 0 8px rgba(0, 217, 163, 0.6);
  border-radius: 2px;
}

/* Active panel — fade in on change (key-driven remount). */
.lp-dash-section {
  flex: 1;
  display: flex; flex-direction: column; gap: 14px;
  min-height: 0;
  animation: lp-dash-fade 400ms ease-in-out;
}
@keyframes lp-dash-fade {
  0%   { opacity: 0; transform: translateY(4px); }
  50%  { opacity: 0.2; }
  100% { opacity: 1; transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  .lp-dash-section { animation: none; }
}
.lp-dash-section-label {
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  letter-spacing: -0.005em;
  margin: 16px 0 16px;
}
.lp-dash-tiles {
  display: flex; flex-wrap: wrap;
  gap: 10px;
}
.lp-dash-tile {
  --t-accent: var(--tile-accent, #00D9A3);
  flex: 1 1 180px;
  min-width: 180px;
  background: linear-gradient(180deg, #141418 0%, #0F0F13 100%);
  border: 1px solid color-mix(in srgb, var(--t-accent) 35%, transparent);
  border-radius: 14px;
  padding: 14px;
  display: flex; flex-direction: column;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 0 0 1px color-mix(in srgb, var(--t-accent) 15%, transparent),
    0 8px 32px rgba(0, 0, 0, 0.4),
    0 0 48px color-mix(in srgb, var(--t-accent) 8%, transparent);
}
.lp-dash-tile-icon { color: var(--t-accent, #00C9A7); }
.lp-dash-tile-head {
  display: flex; align-items: center; gap: 10px;
}
.lp-dash-tile-icon {
  display: inline-flex; align-items: center;
  flex-shrink: 0;
}
.lp-dash-tile-title {
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  letter-spacing: -0.005em;
  line-height: 1.2;
}
.lp-dash-tile-desc {
  font-size: 11px;
  font-weight: 400;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.6);
  margin: 6px 0 0;
}

/* Shopper Intelligence visualisations */
.lp-dash-tile-viz {
  margin-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding-top: 12px;
}
.lp-viz { display: flex; flex-direction: column; gap: 8px; }
.lp-viz-cap {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.55);
  letter-spacing: 0.02em;
}

/* CRM counter */
.lp-viz-crm-num {
  font-size: 30px;
  font-weight: 700;
  color: #00D9A3;
  letter-spacing: -0.02em;
  line-height: 1;
  text-shadow: 0 0 12px rgba(0, 217, 163, 0.4);
}
.lp-viz-crm-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
  margin-top: 4px;
  animation: lp-viz-crm-fade 4s ease-in-out infinite;
}
@keyframes lp-viz-crm-fade {
  0%   { opacity: 0; transform: translateY(4px); }
  10%  { opacity: 1; transform: translateY(0); }
  90%  { opacity: 1; }
  100% { opacity: 0; transform: translateY(-4px); }
}
.lp-viz-crm-avatar {
  display: flex; flex-direction: column;
  align-items: center; gap: 4px;
}
.lp-viz-crm-circle {
  width: 28px; height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
}
.lp-viz-crm-label {
  font-size: 9px;
  color: rgba(255, 255, 255, 0.55);
  white-space: nowrap;
}

/* UK map */
.lp-viz-map {
  display: flex; flex-direction: column;
  gap: 6px; align-items: center;
}
.lp-viz-map-svg {
  height: 80px;
  width: auto;
}
.lp-viz-map-ring {
  transform-origin: center;
  transform-box: fill-box;
  animation: lp-viz-map-pulse 2s ease-out infinite;
  opacity: 0;
}
@keyframes lp-viz-map-pulse {
  0%   { transform: scale(0.4); opacity: 0.9; }
  100% { transform: scale(2.2); opacity: 0;   }
}

/* Weekly bars */
.lp-viz-bars-chart {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  align-items: end;
  height: 80px;
}
.lp-viz-bar-col {
  display: flex; flex-direction: column;
  align-items: stretch; justify-content: flex-end;
  height: 100%;
  gap: 4px;
}
.lp-viz-bar {
  display: block;
  height: var(--h, 50%);
  background: linear-gradient(180deg, #00D9A3 0%, #007a5d 100%);
  border-radius: 2px;
  box-shadow: 0 0 6px rgba(0, 217, 163, 0.28);
  animation: lp-viz-bar-grow 700ms ease-out forwards;
  transform-origin: bottom;
  transform: scaleY(0);
}
@keyframes lp-viz-bar-grow {
  to { transform: scaleY(1); }
}
.lp-viz-bar-label {
  font-size: 9px;
  color: rgba(255, 255, 255, 0.45);
  text-align: center;
  letter-spacing: 0.02em;
}

/* Basket stack */
.lp-viz-basket-bar {
  display: flex;
  width: 100%;
  height: 16px;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
}
.lp-viz-basket-seg {
  display: block;
  height: 100%;
  animation: lp-viz-seg-grow 600ms ease-out forwards;
  transform-origin: left;
  transform: scaleX(0);
}
@keyframes lp-viz-seg-grow {
  to { transform: scaleX(1); }
}
.lp-viz-basket-legend {
  display: flex;
  justify-content: space-between;
  font-size: 9px;
  color: rgba(255, 255, 255, 0.6);
  letter-spacing: 0.02em;
}

/* Conversation — donut */
.lp-viz-donut-row { display: flex; align-items: center; gap: 10px; }
.lp-viz-donut-svg { width: 68px; height: 68px; flex-shrink: 0; }
.lp-viz-donut-legend {
  flex: 1;
  display: flex; flex-direction: column;
  gap: 3px;
  margin: 0; padding: 0; list-style: none;
}
.lp-viz-donut-legend li {
  display: flex; align-items: center; gap: 6px;
  font-size: 10px; color: rgba(255, 255, 255, 0.7);
}
.lp-viz-donut-legend .dot {
  width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0;
}
.lp-viz-donut-legend strong {
  margin-left: auto;
  font-weight: 600;
  color: #fff;
  font-size: 10px;
}

/* Conversation — sentiment */
.lp-viz-sentiment-labels {
  display: flex;
  font-size: 10px;
  font-weight: 600;
}
.lp-viz-sentiment-labels span { text-align: center; }
.lp-viz-sentiment-bar {
  display: flex;
  height: 14px;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
}
.lp-viz-sentiment-bar span { display: block; }
.lp-viz-sentiment-legend {
  display: flex; justify-content: space-between;
  font-size: 9px; color: rgba(255, 255, 255, 0.55);
}

/* Conversation — confusion bubbles */
.lp-viz-bubbles { gap: 4px; }
.lp-viz-bubble {
  display: inline-block;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.85);
  align-self: flex-start;
  animation: lp-viz-bubble-cycle 3s ease-in-out infinite;
}
.lp-viz-bubble:nth-child(2n) { align-self: flex-end; }
@keyframes lp-viz-bubble-cycle {
  0%, 100% { opacity: 0.3; transform: translateX(-4px); }
  30%, 70% { opacity: 1;   transform: translateX(0); }
}

/* Conversation — purchase funnel: label | bar | pct grid */
.lp-viz-funnel { gap: 6px; }
.lp-viz-funnel-row {
  display: grid;
  grid-template-columns: 80px 1fr 40px;
  gap: 8px;
  align-items: center;
}
.lp-viz-funnel-label {
  font-size: 11px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.lp-viz-funnel-track {
  height: 12px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 3px;
  overflow: hidden;
}
.lp-viz-funnel-bar {
  display: block; height: 100%;
  background: linear-gradient(90deg, #0A5E4A, #00F5B3);
  border-radius: 3px;
  box-shadow: 0 0 6px rgba(0, 217, 163, 0.3);
  animation: lp-viz-seg-grow 600ms ease-out forwards;
  transform-origin: left;
  transform: scaleX(0);
}
.lp-viz-funnel-pct {
  font-size: 11px;
  font-weight: 600;
  color: #ffffff;
  text-align: right;
}
@media (prefers-reduced-motion: reduce) {
  .lp-viz-funnel-bar { animation: none; transform: none; }
}

/* Conversation — language pills */
.lp-viz-lang-row { display: flex; flex-wrap: wrap; gap: 4px; }
.lp-viz-lang-pill {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid rgba(0, 217, 163, 0.45);
  font-size: 10px;
  color: rgba(255, 255, 255, 0.75);
}
.lp-viz-lang-pill strong { font-weight: 600; color: #fff; }

/* Distribution — store availability ring */
.lp-viz-availability { align-items: center; text-align: center; }
.lp-viz-avail-ring {
  position: relative;
  width: 56px; height: 56px;
  display: flex; align-items: center; justify-content: center;
}
.lp-viz-avail-ring svg { width: 100%; height: 100%; }
.lp-viz-avail-pct {
  position: absolute;
  font-size: 16px; font-weight: 700;
  color: #00D9A3;
  letter-spacing: -0.02em;
}
.lp-viz-avail-small {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.35;
}

/* Distribution — out-of-stock alerts */
.lp-viz-oos { gap: 5px; }
.lp-viz-oos-row {
  display: flex; align-items: center; gap: 6px;
  font-size: 10px;
}
.lp-viz-oos-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: #E11D48;
  box-shadow: 0 0 6px rgba(225, 29, 72, 0.6);
  flex-shrink: 0;
}
.lp-viz-oos-store { color: rgba(255, 255, 255, 0.85); flex: 1; }
.lp-viz-oos-time { color: rgba(255, 255, 255, 0.5); font-size: 9px; }

/* Distribution — household grid */
.lp-viz-house-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 4px;
  max-width: 110px;
  margin: 0 auto;
}
.lp-viz-house {
  width: 100%;
  height: auto;
  aspect-ratio: 1 / 1;
  color: rgba(255, 255, 255, 0.15);
  stroke: currentColor;
}
.lp-viz-house[data-on="true"] {
  color: #00D9A3;
  filter: drop-shadow(0 0 3px rgba(0, 217, 163, 0.5));
}

/* Campaign — engagement rate */
.lp-viz-engrate { align-items: center; text-align: center; }
.lp-viz-engrate-num {
  font-size: 32px;
  font-weight: 700;
  color: #00D9A3;
  letter-spacing: -0.02em;
  line-height: 1;
  text-shadow: 0 0 12px rgba(0, 217, 163, 0.35);
}
.lp-viz-engrate-compare {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
}
.lp-viz-engrate-compare .up {
  color: #00D9A3;
  font-size: 9px;
  text-shadow: 0 0 4px rgba(0, 217, 163, 0.8);
}

/* Campaign — game funnel */
.lp-viz-gamefunnel { gap: 4px; }
.lp-viz-gf-row {
  display: grid;
  grid-template-columns: 40px 1fr 36px;
  align-items: center;
  gap: 8px;
  font-size: 10px;
}
.lp-viz-gf-label { color: rgba(255, 255, 255, 0.7); }
.lp-viz-gf-track {
  height: 10px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 3px;
  overflow: hidden;
}
.lp-viz-gf-bar {
  display: block; height: 100%;
  border-radius: 3px;
  animation: lp-viz-seg-grow 600ms ease-out forwards;
  transform-origin: left;
  transform: scaleX(0);
}
.lp-viz-gf-pct {
  font-weight: 600;
  color: #fff;
  text-align: right;
}

/* Campaign — redemption blocks */
.lp-viz-rdm-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}
.lp-viz-rdm-block {
  border: 1px solid rgba(0, 217, 163, 0.3);
  border-radius: 8px;
  padding: 8px 4px;
  text-align: center;
  background: rgba(0, 217, 163, 0.04);
}
.lp-viz-rdm-num {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.015em;
  line-height: 1;
}
.lp-viz-rdm-label {
  font-size: 9px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 4px;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

/* Campaign — ROI dashboard */
.lp-viz-roi { align-items: center; text-align: center; }
.lp-viz-roi-row {
  display: flex; align-items: center; gap: 10px;
}
.lp-viz-roi-col { text-align: center; }
.lp-viz-roi-num {
  font-size: 14px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.85);
  letter-spacing: -0.015em;
  line-height: 1;
}
.lp-viz-roi-num--bright {
  color: #00D9A3;
  text-shadow: 0 0 8px rgba(0, 217, 163, 0.35);
}
.lp-viz-roi-label {
  font-size: 9px;
  color: rgba(255, 255, 255, 0.55);
  letter-spacing: 0.03em;
  text-transform: uppercase;
  margin-top: 4px;
}
.lp-viz-roi-arrow {
  color: #00D9A3;
  font-size: 16px;
  font-weight: 700;
}
.lp-viz-roi-return {
  font-size: 14px;
  font-weight: 700;
  color: #00F5B3;
  letter-spacing: 0.01em;
  text-shadow: 0 0 10px rgba(0, 245, 179, 0.5);
}

/* Compliance — ASA score ring */
.lp-viz-compliance { align-items: center; text-align: center; }
.lp-viz-comp-ring {
  position: relative;
  width: 68px; height: 68px;
  display: flex; align-items: center; justify-content: center;
}
.lp-viz-comp-ring svg { width: 100%; height: 100%; }
.lp-viz-comp-pct {
  position: absolute;
  font-size: 14px; font-weight: 700;
  color: #00A86B;
  letter-spacing: -0.015em;
}
.lp-viz-comp-tick {
  position: absolute;
  top: 0; right: 0;
  width: 16px; height: 16px;
  background: rgba(0, 168, 107, 0.15);
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
}
.lp-viz-comp-tick svg { width: 10px; height: 10px; }

/* Compliance — audit logs */
.lp-viz-audit { align-items: center; gap: 8px; }
.lp-viz-audit-stack {
  display: flex; align-items: center; justify-content: center;
  position: relative;
  height: 48px;
  width: 72px;
}
.lp-viz-audit-doc {
  width: 28px; height: 36px;
  position: absolute;
}
.lp-viz-audit-formats {
  display: flex; gap: 4px;
}
.lp-viz-audit-pill {
  font-size: 9px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  padding: 2px 8px;
  border: 1px solid rgba(0, 217, 163, 0.45);
  border-radius: 999px;
  letter-spacing: 0.04em;
}

/* Compliance — violation zero */
.lp-viz-zero { align-items: center; text-align: center; gap: 6px; position: relative; }
.lp-viz-zero-num {
  font-size: 54px;
  font-weight: 700;
  color: #00A86B;
  letter-spacing: -0.03em;
  line-height: 1;
  text-shadow: 0 0 16px rgba(0, 168, 107, 0.45);
}
.lp-viz-zero-tick {
  display: inline-flex; width: 16px; height: 16px;
}

/* Compliance — nutrition engagement bars */
.lp-viz-nutrition { gap: 5px; }
.lp-viz-nut-row {
  display: grid;
  grid-template-columns: 46px 1fr 30px;
  align-items: center;
  gap: 8px;
  font-size: 10px;
}
.lp-viz-nut-label { color: rgba(255, 255, 255, 0.75); }
.lp-viz-nut-track {
  height: 8px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 3px;
  overflow: hidden;
}
.lp-viz-nut-bar {
  display: block; height: 100%;
  border-radius: 3px;
  animation: lp-viz-seg-grow 600ms ease-out forwards;
  transform-origin: left;
  transform: scaleX(0);
}
.lp-viz-nut-pct { color: #fff; font-weight: 600; text-align: right; }

@media (prefers-reduced-motion: reduce) {
  .lp-viz-crm-row,
  .lp-viz-map-ring,
  .lp-viz-bar,
  .lp-viz-basket-seg,
  .lp-viz-bubble,
  .lp-viz-gf-bar,
  .lp-viz-nut-bar { animation: none; opacity: 1; transform: none; }
}

/* Reduced-motion fallback: render all sections stacked vertically. */
.lp-dash-reduced {
  height: auto;
  gap: 24px;
}
.lp-dash-reduced .lp-dash-section { animation: none; }

@media (max-width: 767px) {
  .lp-dash { padding: 20px; }
  .lp-dash-tiles { gap: 10px; }
  /* 1-column tiles on mobile — was 2-col, which clipped titles like
     "Scan-to-engagement" and stacked numbers into each other. */
  .lp-dash-tile {
    flex: 1 1 100%;
    min-width: 0;
    padding: 16px;
    overflow: hidden;
  }
  .lp-dash-tile-title {
    font-size: clamp(14px, 4vw, 18px);
    line-height: 1.2;
    flex: 1 1 auto;
    min-width: 0;
    word-break: break-word;
    overflow-wrap: break-word;
  }
  .lp-dash-tile-desc {
    font-size: clamp(12px, 3.5vw, 14px);
    line-height: 1.5;
    overflow-wrap: break-word;
  }
  .lp-dash-tile-head { min-width: 0; }
  /* Tiles now stack tall, so let the cycle grow with its content. */
  .lp-dash-cycle { height: auto; min-height: 720px; }

  /* ROI tile — stack the two money columns vertically so £14,200 + £48,600
     don't collide. Arrow rotates 90° to point down between them. */
  .lp-viz-roi-row {
    flex-direction: column;
    gap: 8px;
    align-items: center;
  }
  .lp-viz-roi-arrow {
    transform: rotate(90deg);
    font-size: 20px;
  }
  .lp-viz-roi-num { font-size: clamp(18px, 5vw, 24px); }

  /* Redemption tile — collapse 3-col blocks to a single column vertical stack. */
  .lp-viz-rdm-row { grid-template-columns: 1fr; gap: 8px; }
  .lp-viz-rdm-block { padding: 10px 12px; display: flex; align-items: baseline; gap: 10px; justify-content: space-between; }
  .lp-viz-rdm-num { font-size: clamp(20px, 5vw, 24px); }
  .lp-viz-rdm-label { margin-top: 0; }
}

/* ─────────────────────────────────────────────────────────────────────────
   Mobile text-overflow protection (global within the .lp landing scope).
   Applied below 768px so long strings like "Scan-to-engagement" hyphenate
   and wrap rather than getting clipped or forcing horizontal scroll.
   ───────────────────────────────────────────────────────────────────────── */
@media (max-width: 767px) {
  .lp h1, .lp h2, .lp h3, .lp h4, .lp h5, .lp h6,
  .lp p, .lp span, .lp div, .lp li, .lp a, .lp button {
    overflow-wrap: break-word;
    word-wrap: break-word;
    hyphens: manual;
  }
  /* Headings never hyphenate on mobile. They break at word boundaries only,
     with the overflow-wrap fallback catching truly container-breaking tokens
     like "Scan-to-engagement" at their natural hyphen. */
  .lp h1, .lp h2, .lp h3, .lp h4, .lp-hero-title, .lp-steps-title, .lp-journey-title {
    hyphens: none;
    line-height: 1.15;
  }
  /* Dashboard tile titles wrap via break-word for compound words. */
  .lp-dash-tile-title, .lp-entry-card-title {
    word-break: normal;
    overflow-wrap: anywhere;
    hyphens: none;
  }
  /* min-width: 0 lets flex/grid children shrink below their intrinsic content
     width — required for the above word-wrap to actually take effect. */
  .lp-dash-tile, .lp-entry-card, .lp-pv-card,
  .lp-journey-col, .lp-viz, .lp-viz-rdm-block { min-width: 0; }

  /* Journey caption was "white-space: nowrap + overflow-x: auto" which caused
     a horizontal scroll strip on mobile. Allow it to wrap normally instead. */
  .lp-journey-caption {
    white-space: normal;
    overflow-x: visible;
    text-align: center;
    line-height: 1.6;
    font-size: 13px;
  }

  /* FAQ / stats / any other multi-col grid in the page: collapse to 1 col.
     Integrations logo strip stays at 2 cols (logos are compact). */
  .lp-faq-grid, .lp-stats-grid, .lp-compare-grid, .lp-use-cases-grid,
  .lp-pnm-grid, .lp-modules-grid {
    grid-template-columns: minmax(0, 1fr) !important;
    gap: 12px;
  }
  .lp-integrations-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
}

.lp-journey-caption {
  margin-top: 48px;
  font-size: 14px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.6);
  max-width: none;
  text-align: center;
  white-space: nowrap;
  overflow-x: auto;
}

/* ────────── Footer (§6.14) ────────── */
.lp-footer {
  background: var(--ink);
  color: var(--paper);
  /* Footer paddings tightened in v3.1: top 96→56px, bottom kept at 24px.
     The original 96px was sized for a 4-column footer that doesn't exist
     anymore (Resources column was removed) — combined with the empty 4th
     grid slot below it produced the dead-air the team flagged. */
  padding: 56px var(--gutter) var(--space-5);
}
.lp-footer-top {
  max-width: var(--container);
  margin-inline: auto;
  display: grid;
  /*
    3 columns for the actual 3 sections (Platform / Modules / Company).
    Was previously repeat(4, 1fr) which left a phantom blank column. Column
    widths are weighted: Modules has 6 items, Platform has 4, Company has 2,
    so a wider Modules column keeps the visual balance even.
  */
  grid-template-columns: 1.1fr 1.1fr 0.8fr;
  gap: var(--space-6);
  padding-bottom: var(--space-6);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
/* Tablet: keep 3 equal columns rather than wrapping Company onto its own
   row (which produced a long blank ribbon). The content is light enough
   that 3-up holds at iPad-portrait widths. Stack to single column at
   phone width where horizontal space genuinely runs out. */
@media (max-width: 1023px) { .lp-footer-top { grid-template-columns: repeat(3, 1fr); gap: var(--space-5); } }
@media (max-width: 767px)  { .lp-footer-top { grid-template-columns: 1fr; gap: var(--space-5); padding-bottom: var(--space-5); } }
.lp-footer-col-head {
  font-size: var(--size-micro);
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--paper);
  margin-bottom: var(--space-4);
}
.lp-footer-col ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 10px;
}
.lp-footer-col a {
  color: rgba(255, 255, 255, 0.78);
  text-decoration: none;
  font-size: var(--size-small);
  transition: color var(--dur-micro) var(--ease-ui);
}
.lp-footer-col a:hover { color: var(--accent); }

.lp-footer-mid {
  max-width: var(--container);
  margin-inline: auto;
  /* Was --space-7 (48px) top+bottom — over-padded for a one-line newsletter
     prompt. 24px reads tighter without crowding the input. */
  padding-block: var(--space-5);
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: var(--space-5);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
@media (max-width: 767px) {
  .lp-footer-mid {
    grid-template-columns: 1fr;
    padding-inline: 0;
    padding-block: var(--space-4);
    gap: var(--space-3);
  }
  .lp-footer-news-form { flex-wrap: wrap; }
  .lp-footer-news-input { min-width: 0; flex: 1 1 100%; }
  .lp-footer-news-submit { flex: 0 0 auto; }
  .lp-footer-news-heading { font-size: var(--size-body-lg); max-width: none; }
}

/* Page-level guard: prevent any descendant from leaking horizontal scroll.
   Individual sections (ticker, dash-tabs) already handle their own internal
   scrolling; this just stops stray overflow from turning into a page-wide
   horizontal scrollbar on mobile. */
@media (max-width: 767px) {
  .lp, body, html { overflow-x: clip; }
}

/* ─────────────────────────────────────────────────────────────────────────
   Mobile spacing compaction — tighten vertical rhythm across sections so
   the page feels purposeful rather than airy. Anchored min values: 16px
   gutter, 32px section vertical, 12px between related elements.
   ───────────────────────────────────────────────────────────────────────── */
@media (max-width: 767px) {
  /* Section vertical paddings */
  .lp-steps { padding: 48px 0; }
  .lp-integrations { padding: 48px 0; }
  .lp-journey { padding: 32px 0 48px; }

  /* Three-step section head was margin-bottom: 120px — way too much on a
     stacked mobile layout. Pull the first circle closer to its header. */
  .lp-steps-head { margin-bottom: 32px; gap: 12px; }

  /* Pull the dashboard section-label closer to the tiles. */
  .lp-dash-section-label { margin: 12px 0 12px; font-size: 16px; }

  /* Journey head → shell gap */
  .lp-journey-head { gap: 12px; }
  .lp-journey-head + .lp-journey-shell,
  .lp-journey-shell { margin-top: 16px; }

  /* Trust strip caption below journey */
  .lp-journey-caption { margin-top: 20px; font-size: 12px; line-height: 1.5; }

  /* Integrations headline block */
  .lp-integrations-inner > * + * { margin-top: 12px; }

  /* Inter-card gaps — keep at 16px where the stack is tall, 12px where it
     is a denser list (already tightened in Fix 1). */
  .lp-journey-grid { gap: 24px; }
  .lp-entry-card + .lp-entry-card { margin-top: 0; }
}
.lp-footer-news-heading {
  font-size: var(--size-h4);
  font-weight: 600;
  color: var(--paper);
  max-width: 30ch;
}
.lp-footer-news-form {
  display: flex;
  gap: 10px;
  align-items: stretch;
}
.lp-footer-news-input {
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.14);
  color: var(--paper);
  font-family: var(--font-sans);
  font-size: var(--size-small);
  padding: 12px 16px;
  border-radius: 999px;
  min-width: 260px;
  outline: none;
  transition: border-color var(--dur-micro) var(--ease-ui), background var(--dur-micro) var(--ease-ui);
}
.lp-footer-news-input:focus { border-color: var(--accent); background: rgba(255,255,255,0.10); }
.lp-footer-news-input::placeholder { color: rgba(255,255,255,0.46); }
.lp-footer-news-submit {
  background: var(--accent);
  color: var(--paper);
  border: 0;
  padding: 12px 20px;
  border-radius: 999px;
  font-family: var(--font-sans);
  font-weight: 600;
  font-size: var(--size-small);
  cursor: pointer;
  transition: background var(--dur-micro) var(--ease-ui);
}
.lp-footer-news-submit:hover { background: var(--accent-deep); }

.lp-footer-bot {
  max-width: var(--container);
  margin-inline: auto;
  padding-top: var(--space-4);
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: var(--space-5);
  color: rgba(255,255,255,0.6);
  font-size: 13px;
}
.lp-footer-bot .center { text-align: center; }
.lp-footer-bot .right  { justify-self: end; display: inline-flex; gap: 14px; align-items: center; }
.lp-footer-bot .right a { color: rgba(255,255,255,0.8); display: inline-flex; align-items: center; transition: color var(--dur-micro) var(--ease-ui); }
.lp-footer-bot .lp-footer-legal-link { font-size: 13px; padding-right: 4px; }
.lp-footer-bot .lp-footer-legal-link + .lp-footer-legal-link { border-left: 1px solid rgba(255,255,255,0.2); padding-left: 12px; padding-right: 8px; }
.lp-footer-bot .right a:hover { color: var(--accent); }
.lp-footer-admin-btn { background: transparent; border: 0; padding: 0; cursor: pointer; color: rgba(255,255,255,0.4); display: inline-flex; align-items: center; transition: color var(--dur-micro) var(--ease-ui); }
.lp-footer-admin-btn:hover { color: var(--accent); }
@media (max-width: 767px) {
  .lp-footer-bot { grid-template-columns: 1fr; text-align: center; gap: var(--space-3); padding-top: var(--space-3); font-size: 12px; }
  .lp-footer-bot .right { justify-self: center; }
}

/* ────────── Footer mobile compaction (v3.1) ──────────
   The desktop footer padding (56px top, 24px bottom) was excessive on
   phones — every section has its own border + padding so the cumulative
   vertical was ~360px on a 600px-tall screen. This rule halves the outer
   padding on small viewports and tightens column-head sizing too. */
@media (max-width: 767px) {
  .lp-footer { padding: var(--space-6) var(--gutter) var(--space-4); }
  .lp-footer-col-head { font-size: 11px; margin-bottom: var(--space-3); }
  .lp-footer-col ul { gap: var(--space-2); }
}

/* ────────── Footer link buttons (Platform / Modules) ──────────
   The Platform + Modules columns use <button> instead of <a> because the
   click opens an in-page modal rather than navigating. We strip the native
   button chrome so visually they're indistinguishable from the <a> rows
   above. Same hover colour, same font weight, left-aligned. */
.lp-footer-link-btn {
  appearance: none;
  background: transparent;
  border: 0;
  padding: 0;
  margin: 0;
  font: inherit;
  text-align: left;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.78);
  font-size: var(--size-small);
  transition: color var(--dur-micro) var(--ease-ui);
}
.lp-footer-link-btn:hover { color: var(--accent); }
.lp-footer-link-btn:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
  border-radius: 2px;
}

/* ────────── InfoModal — opens from the Platform / Modules footer links ──────────
   Centred glass card overlaid on a blurred backdrop. The animation is
   short (300ms total) so it doesn't fight the reduced-motion override
   below. Sized for prose: max-width is reading-width territory, body text
   pre-wrapped so paragraph line breaks survive verbatim from the source. */
.lp-info-overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-5);
  background: rgba(11, 11, 15, 0.72);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  animation: lp-info-fade-in var(--dur-micro) var(--ease-enter);
}
.lp-info-card {
  position: relative;
  max-width: 640px;
  width: 100%;
  max-height: calc(100vh - var(--space-7));
  overflow-y: auto;
  background:
    linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%),
    rgba(11, 11, 15, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  /* Padding lives on .lp-info-content (not the card) so an optional hero
     image can sit edge-to-edge against the card border. The card clips
     overflow so the image's top corners follow the card's border-radius. */
  overflow-x: hidden;
  padding: 0;
  box-shadow:
    0 32px 80px -20px rgba(0, 0, 0, 0.6),
    0 0 0 1px rgba(255, 255, 255, 0.04) inset;
  animation: lp-info-pop-in 320ms var(--ease-enter);
}
.lp-info-content {
  padding: var(--space-6) var(--space-6) var(--space-7);
}
.lp-info-image-wrap {
  /* Banner block that hosts the optional hero image. Lets the image set its
     own height at the rendered width — no aggressive cropping. The dark
     background fills any letterbox area for non-3:2 aspect ratios so
     letterboxing reads as intentional rather than broken. */
  position: relative;
  width: 100%;
  background: rgba(255, 255, 255, 0.03);
  overflow: hidden;
}
.lp-info-image {
  /* Was object-fit: cover with max-height: 280px — that produced a ~34%
     vertical crop on the 3:2 hero images (1536×1024 source becomes
     640×426 natural at modal width, but the 280 cap forced cover-crop).
     'contain' + a generous max-height shows the whole image; the cap only
     activates if a future hero is unusually tall. */
  display: block;
  width: 100%;
  height: auto;
  max-height: 480px;
  object-fit: contain;
  object-position: center;
}
/* When the card has an image, the close button overlays it. The existing
   translucent-dark fill is dark enough for legibility on most photos, but
   we add a soft drop-shadow ring for extra contrast over busy imagery. */
.lp-info-card--with-image .lp-info-close {
  background: rgba(11, 11, 15, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border-color: rgba(255, 255, 255, 0.16);
  box-shadow: 0 4px 16px -4px rgba(0, 0, 0, 0.5);
}
.lp-info-card--with-image .lp-info-content {
  /* Title sits closer to the image than the original top padding to make
     the image feel attached rather than floating. */
  padding-top: var(--space-5);
}
.lp-info-close {
  position: absolute;
  top: var(--space-3);
  right: var(--space-3);
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.7);
  font-size: 22px;
  line-height: 1;
  transition:
    color var(--dur-micro) var(--ease-ui),
    background-color var(--dur-micro) var(--ease-ui),
    border-color var(--dur-micro) var(--ease-ui);
}
.lp-info-close:hover {
  color: var(--paper);
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.18);
}
.lp-info-close:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
.lp-info-title {
  margin: 0 0 var(--space-4);
  padding-right: var(--space-7); /* room for the close button */
  font-size: var(--size-h3);
  font-weight: 700;
  color: var(--paper);
  letter-spacing: -0.01em;
}
.lp-info-body {
  margin: 0;
  font-size: var(--size-body);
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.82);
  white-space: pre-line; /* preserve paragraph breaks from the source content */
}
@keyframes lp-info-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes lp-info-pop-in {
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0)  scale(1); }
}
@media (max-width: 640px) {
  /* Padding moved off the card and onto .lp-info-content in the v3.1 image
     refactor; the mobile rule has to target the new selector or the card
     reverts to no inner padding at all on phones. */
  .lp-info-content { padding: var(--space-5) var(--space-5) var(--space-6); }
  .lp-info-card--with-image .lp-info-content { padding-top: var(--space-4); }
  .lp-info-title { font-size: var(--size-h4); }
  /* Mobile cap: at ~340px modal width with 3:2 aspect natural = 226px tall.
     Allow up to 280 so the whole image shows; rule only kicks in for hero
     images taller than 4:5. */
  .lp-info-image { max-height: 280px; }
}

/* ────────── Reduced motion ────────── */
@media (prefers-reduced-motion: reduce) {
  .lp *, .lp *::before, .lp *::after {
    animation-duration: 0.001ms !important;
    transition-duration: 0.001ms !important;
  }
}
`;
